// src/pages/admin/BranchReports.tsx
import React, { useState, useEffect, useMemo } from "react";
import toast from "react-hot-toast";
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Download,
  BarChart3,
  LineChart,
  Users,
  CreditCard,
  Filter,
  RefreshCw,
  Building,
  GraduationCap,
  UserCheck,
  UserX,
  BookOpen,
  Award,
  Calendar,
  Search,
  FileText,
  PieChart as PieChartIcon,
  Sparkles,
} from "lucide-react";
import {
  ResponsiveContainer,
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  BarChart as RechartsBarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Area,
  AreaChart,
} from "recharts";
import { financialReportsService } from "../../services/financial-reports";
import {
  FinancialReportFilters,
  ComprehensiveFinancialReport,
  FinancialKPI,
} from "../../types/financial-reports";
import { useBranch } from "../../contexts/BranchContext";
import { useAuth } from "../../hooks/useAuth";
import AiAnalyticsModal from "../../components/reports/AiAnalyticsModal";
import { getStudents, getStudentStats } from "../../services/students";
import {
  getDepartments,
  getDepartmentStatistics,
} from "../../services/departments";
import { getCourses } from "../../services/courses";
import { getTeachers, getTeacherStats } from "../../services/teachers";
import { getBranchById } from "../../services/branches";
import { getAttendanceDashboard } from "../../services/attendance";

const BranchReports: React.FC = () => {
  const { selectedBranch } = useBranch();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] =
    useState<ComprehensiveFinancialReport | null>(null);
  const [kpis, setKpis] = useState<FinancialKPI[]>([]);
  const [activeTab, setActiveTab] = useState<string>("overview");
  const [reportCategory, setReportCategory] = useState<
    "financial" | "academic" | "operational"
  >("financial");
  const [searchQuery, setSearchQuery] = useState("");
  const [isAiModalOpen, setIsAiModalOpen] = useState(false);

  // Additional statistics states
  const [studentStats, setStudentStats] = useState<any>(null);
  const [departmentStats, setDepartmentStats] = useState<any[]>([]);
  const [teacherStats, setTeacherStats] = useState<any>(null);
  const [courseStats, setCourseStats] = useState<any[]>([]);
  const [selectedDepartment, setSelectedDepartment] = useState<string>("all");
  const [branchName, setBranchName] = useState<string>("");
  const [selectedCourse, setSelectedCourse] = useState<string>("all");
  const [attendanceStats, setAttendanceStats] = useState<any>(null);

  // Compute effective teacher count from department stats when teacherStats is missing/zero
  const effectiveTeacherCount = useMemo(() => {
    if (teacherStats?.totalTeachers) return teacherStats.totalTeachers;
    if (!departmentStats?.length) return 0;
    return departmentStats.reduce(
      (sum: number, d: any) =>
        sum + (d.staffCount || d.statistics?.totalTeachers || 0),
      0,
    );
  }, [teacherStats, departmentStats]);

  const effectiveActiveTeachers = useMemo(() => {
    if (teacherStats?.activeTeachers) return teacherStats.activeTeachers;
    // If we're falling back to department stats, assume all staff are active
    return effectiveTeacherCount;
  }, [teacherStats, effectiveTeacherCount]);

  // Compute effective student-teacher ratio
  const effectiveStudentTeacherRatio = useMemo(() => {
    if (teacherStats?.studentTeacherRatio)
      return teacherStats.studentTeacherRatio;
    if (!effectiveTeacherCount || !studentStats?.totalStudents) return 0;
    return Math.round(studentStats.totalStudents / effectiveTeacherCount);
  }, [teacherStats, effectiveTeacherCount, studentStats]);

  // Reset active tab when category changes
  useEffect(() => {
    if (reportCategory === "financial") setActiveTab("overview");
    if (reportCategory === "academic") setActiveTab("students");
    if (reportCategory === "operational") setActiveTab("overview");
  }, [reportCategory]);

  // Determine which branch to use - for branch admins, use their branchId
  const effectiveBranchId = user?.branchId
    ? String(user.branchId)
    : selectedBranch?._id;

  const isBranchAdmin = user?.roles?.includes("branchadmin");

  // Filter states - initialize without branchId first
  const [filters, setFilters] = useState<FinancialReportFilters>({
    startDate: new Date(new Date().getFullYear(), 0, 1)
      .toISOString()
      .split("T")[0],
    endDate: new Date().toISOString().split("T")[0],
    // branchId will be set in useEffect when effectiveBranchId is available
  });

  // Available time ranges
  const timeRanges = [
    { label: "Last 30 Days", days: 30 },
    { label: "Last 3 Months", days: 90 },
    { label: "Last 6 Months", days: 180 },
    { label: "This Year", days: 365 },
    { label: "Custom Range", days: null },
  ];

  // Load reports data
  const loadReportsData = async () => {
    if (!effectiveBranchId) {
      toast.error("No branch available for reports");
      return;
    }

    setLoading(true);
    try {
      // Use the proper backend services for accurate financial calculations
      const branchFilters = {
        ...filters,
        branchId: effectiveBranchId,
      };

      // Build date filter object for statistics APIs
      const dateFilters =
        filters.startDate && filters.endDate
          ? { startDate: filters.startDate, endDate: filters.endDate }
          : undefined;

      const [
        comprehensiveData,
        kpisData,
        studentsStatsData,
        teachersStatsData,
        attendanceDashboardData,
      ] = await Promise.all([
        financialReportsService.getComprehensiveReport(branchFilters),
        financialReportsService.getFinancialKPIs(branchFilters),
        getStudentStats(effectiveBranchId, dateFilters),
        getTeacherStats(effectiveBranchId, dateFilters),
        getAttendanceDashboard("month", "student").catch(() => ({
          success: false,
          data: null,
        })),
      ]);

      setReportData(comprehensiveData.data);
      setKpis(kpisData.data);
      setStudentStats(studentsStatsData.data);
      setTeacherStats(teachersStatsData.data);
      if (attendanceDashboardData?.success && attendanceDashboardData?.data) {
        setAttendanceStats(attendanceDashboardData.data);
      }

      // Get department, course, and teacher data for statistics
      const [departmentsData, coursesData, teachersData] = await Promise.all([
        getDepartments(1, 1000, undefined, effectiveBranchId, undefined, true),
        getCourses({ limit: 1000 }),
        getTeachers({ limit: 1000 }),
      ]);

      // Process department statistics - count teachers and courses from their respective models
      if (departmentsData?.data) {
        const allTeachers = teachersData?.data || [];
        const allCourses = coursesData?.data || [];

        const deptStats = await Promise.all(
          departmentsData.data.map(async (dept: any) => {
            try {
              const studentsInDept = await getStudents({
                departmentId: dept._id,
                limit: 1000,
              });
              const totalStudents = studentsInDept.data.length;
              const activeStudents = studentsInDept.data.filter(
                (s: any) => s.academicStatus === "active",
              ).length;

              // Count teachers belonging to this department from Teacher model
              const teachersInDept = allTeachers.filter(
                (t: any) =>
                  t.department === dept._id || t.department?._id === dept._id,
              );

              // Count courses belonging to this department from Course model
              const coursesInDept = allCourses.filter(
                (c: any) =>
                  c.departmentId === dept._id ||
                  c.departmentId?._id === dept._id,
              );

              return {
                ...dept,
                staffCount: teachersInDept.length,
                courseCount: coursesInDept.length,
                statistics: {
                  totalStudents,
                  activeStudents,
                  totalTeachers: teachersInDept.length,
                  totalCourses: coursesInDept.length,
                },
              };
            } catch (error) {
              console.error(
                `Error fetching stats for department ${dept._id}:`,
                error,
              );
              return {
                ...dept,
                statistics: {
                  totalStudents: 0,
                  activeStudents: 0,
                  totalTeachers: 0,
                  totalCourses: 0,
                },
              };
            }
          }),
        );
        setDepartmentStats(deptStats);
      }

      // Process course statistics
      if (coursesData?.data) {
        const courseStatsWithStudents = await Promise.all(
          coursesData.data.map(async (course: any) => {
            try {
              const studentsInCourse = await getStudents({
                courseId: course._id,
                limit: 1000,
              });
              return {
                ...course,
                enrolledStudents: studentsInCourse.data.length,
              };
            } catch (error) {
              console.error(
                `Error fetching students for course ${course._id}:`,
                error,
              );
              return {
                ...course,
                enrolledStudents: 0,
              };
            }
          }),
        );
        setCourseStats(courseStatsWithStudents);
      }

      toast.success("Reports loaded successfully");
    } catch (error: any) {
      const errorMessage = error.message || "Failed to load reports";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Initialize data on component mount
  useEffect(() => {
    if (effectiveBranchId) {
      loadReportsData();
    }
  }, [effectiveBranchId]);

  // Fetch branch name for branch admins who don't have selectedBranch
  useEffect(() => {
    const fetchBranchName = async () => {
      if (!selectedBranch && effectiveBranchId) {
        try {
          const response = await getBranchById(effectiveBranchId);
          if (response.success && response.data) {
            setBranchName(response.data.name);
          }
        } catch (error) {
          console.error("Error fetching branch name:", error);
        }
      } else if (selectedBranch) {
        setBranchName(selectedBranch.name);
      }
    };
    fetchBranchName();
  }, [effectiveBranchId, selectedBranch]);

  // Update branch filter when effectiveBranchId changes
  useEffect(() => {
    if (effectiveBranchId) {
      setFilters((prev) => ({
        ...prev,
        branchId: effectiveBranchId,
      }));
    }
  }, [effectiveBranchId]);

  const handleTimeRangeChange = (days: number | null) => {
    if (days === null) {
      // Custom range - keep current dates
      return;
    }

    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);

    setFilters((prev) => ({
      ...prev,
      startDate: startDate.toISOString().split("T")[0],
      endDate: endDate.toISOString().split("T")[0],
    }));
  };

  const handleFilterChange = (
    key: keyof FinancialReportFilters,
    value: any,
  ) => {
    // Prevent changing branchId - always use current branch
    if (key === "branchId") return;

    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  // Helper function to convert data to CSV
  const convertToCSV = (data: any[], headers: string[]): string => {
    const headerRow = headers.join(",");
    const dataRows = data.map((row) =>
      headers
        .map((header) => {
          const value = row[header];
          // Handle values with commas or quotes
          if (
            typeof value === "string" &&
            (value.includes(",") || value.includes('"'))
          ) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value ?? "";
        })
        .join(","),
    );
    return [headerRow, ...dataRows].join("\n");
  };

  // Helper function to download CSV
  const downloadCSV = (csvContent: string, filename: string) => {
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  // Export academic report as CSV
  const exportAcademicReportCSV = () => {
    const exportBranchName = selectedBranch?.name || branchName || "branch";
    const sanitizedBranchName = exportBranchName.replace(/[^a-zA-Z0-9]/g, "-");
    const dateStr = new Date().toISOString().split("T")[0];

    if (activeTab === "students" && studentStats) {
      // Student statistics CSV
      const summaryData = [
        { Metric: "Total Students", Value: studentStats.totalStudents || 0 },
        { Metric: "Active Students", Value: studentStats.activeStudents || 0 },
        {
          Metric: "Inactive Students",
          Value: studentStats.inactiveStudents || 0,
        },
        {
          Metric: "New Enrollments (This Month)",
          Value: studentStats.newEnrollmentsThisMonth || 0,
        },
        {
          Metric: "New Enrollments (This Year)",
          Value: studentStats.newEnrollmentsThisYear || 0,
        },
        {
          Metric: "Enrollment Growth",
          Value: `${studentStats.enrollmentGrowth || 0}%`,
        },
        {
          Metric: "Retention Rate",
          Value: `${studentStats.retentionRate || 0}%`,
        },
        { Metric: "Dropout Rate", Value: `${studentStats.dropoutRate || 0}%` },
        {
          Metric: "Graduated (This Year)",
          Value: studentStats.graduatedThisYear || 0,
        },
        {
          Metric: "Gender Ratio (M:F)",
          Value: studentStats.genderRatio || "N/A",
        },
      ];
      const csv = convertToCSV(summaryData, ["Metric", "Value"]);
      downloadCSV(
        csv,
        `${sanitizedBranchName}-student-statistics-${dateStr}.csv`,
      );
      return true;
    }

    if (activeTab === "teachers" && teacherStats) {
      // Teacher statistics CSV
      const summaryData = [
        {
          Metric: "Total Teachers",
          Value: teacherStats.totalTeachers || effectiveTeacherCount || 0,
        },
        {
          Metric: "Active Teachers",
          Value: teacherStats.activeTeachers || effectiveActiveTeachers || 0,
        },
        {
          Metric: "Inactive Teachers",
          Value: teacherStats.inactiveTeachers || 0,
        },
        {
          Metric: "New Hires (This Month)",
          Value: teacherStats.newHiresThisMonth || 0,
        },
        {
          Metric: "New Hires (This Year)",
          Value: teacherStats.newHiresThisYear || 0,
        },
        {
          Metric: "Student-Teacher Ratio",
          Value:
            teacherStats.studentTeacherRatio ||
            effectiveStudentTeacherRatio ||
            0,
        },
        {
          Metric: "Average Experience (Years)",
          Value: teacherStats.averageExperience || 0,
        },
        {
          Metric: "Average Attendance Rate",
          Value: `${teacherStats.averageAttendanceRate || 0}%`,
        },
      ];
      const csv = convertToCSV(summaryData, ["Metric", "Value"]);
      downloadCSV(
        csv,
        `${sanitizedBranchName}-teacher-statistics-${dateStr}.csv`,
      );
      return true;
    }

    if (activeTab === "departments" && departmentStats.length > 0) {
      // Department statistics CSV
      const deptData = departmentStats.map((dept: any) => ({
        Department: dept.name || "Unknown",
        Code: dept.code || "N/A",
        "Total Students": dept.statistics?.totalStudents || 0,
        "Active Students": dept.statistics?.activeStudents || 0,
        "Total Teachers":
          dept.staffCount || dept.statistics?.totalTeachers || 0,
        "Total Courses": dept.courseCount || dept.statistics?.totalCourses || 0,
      }));
      const csv = convertToCSV(deptData, [
        "Department",
        "Code",
        "Total Students",
        "Active Students",
        "Total Teachers",
        "Total Courses",
      ]);
      downloadCSV(
        csv,
        `${sanitizedBranchName}-department-statistics-${dateStr}.csv`,
      );
      return true;
    }

    if (activeTab === "courses" && courseStats.length > 0) {
      // Course statistics CSV
      const courseData = courseStats.map((course: any) => ({
        Course: course.name || "Unknown",
        Code: course.code || "N/A",
        Department: course.department?.name || "Unassigned",
        "Enrolled Students": course.enrolledStudents || 0,
        Duration: course.duration || "N/A",
        Status: course.isActive ? "Active" : "Inactive",
      }));
      const csv = convertToCSV(courseData, [
        "Course",
        "Code",
        "Department",
        "Enrolled Students",
        "Duration",
        "Status",
      ]);
      downloadCSV(
        csv,
        `${sanitizedBranchName}-course-statistics-${dateStr}.csv`,
      );
      return true;
    }

    return false;
  };

  const handleExportReport = async (format: "pdf" | "excel" | "csv") => {
    if (!effectiveBranchId) {
      toast.error("No branch available for export");
      return;
    }

    if (!filters.startDate || !filters.endDate) {
      toast.error("Please select a date range for the report");
      return;
    }

    // For academic reports with CSV only, use client-side export for just academic data
    if (reportCategory === "academic" && format === "csv") {
      const exported = exportAcademicReportCSV();
      if (exported) {
        toast.success("Academic report exported as CSV");
        return;
      }
      toast.error("No data available to export");
      return;
    }

    const exportToast = toast.loading(
      `Generating comprehensive ${format.toUpperCase()} report...`,
    );

    try {
      // Export comprehensive report including all data
      const blob = await financialReportsService.exportFinancialReport({
        format,
        includeCharts: true,
        includeDetails: true,
        comprehensiveReport: true, // Include all dashboard data: financial, academic, operational
        dateRange: {
          startDate: filters.startDate,
          endDate: filters.endDate,
        },
        branchIds: [effectiveBranchId],
      });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;

      // Generate filename with branch name and date range
      const exportBranchName = selectedBranch?.name || branchName || "branch";
      const sanitizedBranchName = exportBranchName.replace(
        /[^a-zA-Z0-9]/g,
        "-",
      );
      const dateStr = new Date().toISOString().split("T")[0];
      a.download = `${sanitizedBranchName}-comprehensive-report-${dateStr}.${
        format === "excel" ? "xlsx" : format
      }`;

      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success(
        `Comprehensive report exported as ${format.toUpperCase()}`,
        {
          id: exportToast,
        },
      );
    } catch (error: any) {
      console.error("Export error:", error);
      const errorMessage = error.message || "Failed to export report";
      toast.error(errorMessage, { id: exportToast });
    }
  };

  const formatCurrency = (amount: number | null | undefined) => {
    return new Intl.NumberFormat("en-KE", {
      style: "currency",
      currency: "KES",
      currencyDisplay: "code",
    })
      .format(amount || 0)
      .replace("KES", "KSh");
  };

  const formatPercentage = (value: number | null | undefined) => {
    return `${(value || 0).toFixed(1)}%`;
  };

  const getKPIIcon = (name: string) => {
    switch (name.toLowerCase()) {
      case "revenue":
      case "total revenue":
        return <DollarSign className="w-3 h-3" />;
      case "profit margin":
      case "net profit":
        return <TrendingUp className="w-3 h-3" />;
      case "collection rate":
        return <CreditCard className="w-3 h-3" />;
      case "student count":
        return <Users className="w-3 h-3" />;
      case "branch performance":
        return <Building className="w-5 h-5" />;
      default:
        return <BarChart3 className="w-5 h-5" />;
    }
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case "up":
        return <TrendingUp className="w-4 h-4 text-green-500" />;
      case "down":
        return <TrendingDown className="w-4 h-4 text-red-500" />;
      default:
        return <div className="w-4 h-4 bg-gray-400 rounded-full" />;
    }
  };

  if (!effectiveBranchId) {
    // Only show "No Branch Selected" for non-branch admins
    // Branch admins should always have their branchId from user context
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-12">
          <div className="text-center">
            <div className="bg-gradient-to-br from-amber-300 to-orange-400 rounded-2xl p-4 w-fit mx-auto mb-4">
              <Building className="h-12 w-12 text-white" />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">
              No Branch Selected
            </h3>
            <p className="text-slate-600 font-medium">
              {isBranchAdmin
                ? "Unable to determine your branch. Please contact administrator."
                : "Please select a branch to view financial reports."}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (loading && !reportData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center p-6">
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-12">
          <div className="flex flex-col items-center justify-center gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-slate-200 border-t-blue-500"></div>
            <p className="text-slate-600 font-medium">
              Loading branch financial reports...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6 space-y-6">
      {/* Header */}
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="bg-gradient-to-br from-blue-300 to-indigo-400 rounded-2xl p-3 shadow-lg">
              <BarChart3 className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">
                Branch Reports & Analytics
              </h1>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <div className="relative">
              <input
                type="text"
                placeholder="Search reports..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2.5 border-2 border-slate-200 rounded-xl font-medium focus:border-blue-400 focus:outline-none w-full sm:w-64"
              />
            </div>
            <button
              onClick={loadReportsData}
              disabled={loading}
              className="bg-white border-2 border-slate-200 px-5 py-2.5 rounded-xl font-bold shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              <RefreshCw
                className={`h-5 w-5 text-blue-500 ${
                  loading ? "animate-spin" : ""
                }`}
              />
              <span className="text-slate-700">Refresh</span>
            </button>
            {/* <button
              onClick={() => setIsAiModalOpen(true)}
              className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-5 py-2.5 rounded-xl font-bold shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2"
            >
              <Sparkles className="h-5 w-5" />
              <span>Ask AI</span>
            </button> */}
            <div className="relative">
              <select
                onChange={(e) => handleExportReport(e.target.value as any)}
                value=""
                className="bg-white border-2 border-slate-200 px-5 py-2.5 pr-10 rounded-xl font-bold shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300 text-slate-700 appearance-none focus:outline-none focus:border-blue-400"
              >
                <option value="" disabled>
                  📥 Export Full Report
                </option>
                <option value="pdf">📄 Complete Report (PDF)</option>
                <option value="excel">📊 Complete Report (Excel)</option>
                {reportCategory === "academic" && (
                  <option value="csv">📋 Academic Data (CSV)</option>
                )}
              </select>
            </div>
          </div>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-1">
          <button
            onClick={() => setReportCategory("financial")}
            className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
              reportCategory === "financial"
                ? "bg-blue-50 text-blue-600 border-b-2 border-blue-500"
                : "text-slate-600 hover:bg-slate-50"
            }`}
          >
            <div className="flex items-center gap-2">
              <DollarSign className="w-4 h-4" />
              Financial
            </div>
          </button>
          <button
            onClick={() => setReportCategory("academic")}
            className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
              reportCategory === "academic"
                ? "bg-blue-50 text-blue-600 border-b-2 border-blue-500"
                : "text-slate-600 hover:bg-slate-50"
            }`}
          >
            <div className="flex items-center gap-2">
              <GraduationCap className="w-4 h-4" />
              Academic
            </div>
          </button>
          <button
            onClick={() => setReportCategory("operational")}
            className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
              reportCategory === "operational"
                ? "bg-blue-50 text-blue-600 border-b-2 border-blue-500"
                : "text-slate-600 hover:bg-slate-50"
            }`}
          >
            <div className="flex items-center gap-2">
              <Building className="w-4 h-4" />
              Operational
            </div>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
        <div className="flex items-center gap-2 mb-6">
          <Filter className="h-5 w-5 text-blue-500" />
          <h3 className="text-lg font-bold text-slate-900">Report Filters</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Time Range Quick Select */}
          <div className="space-y-2">
            <label className="text-sm font-bold text-slate-700">
              Time Range
            </label>
            <select
              onChange={(e) => {
                const days = parseInt(e.target.value);
                handleTimeRangeChange(isNaN(days) ? null : days);
              }}
              className="w-full px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200 rounded-xl font-bold text-slate-700 focus:border-blue-400 focus:outline-none transition-all"
            >
              <option value="">Select Range</option>
              {timeRanges.map((range) => (
                <option key={range.label} value={range.days || ""}>
                  {range.label}
                </option>
              ))}
            </select>
          </div>

          {/* Start Date */}
          <div className="space-y-2">
            <label className="text-sm font-bold text-slate-700">
              Start Date
            </label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange("startDate", e.target.value)}
              className="w-full px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200 rounded-xl font-medium text-slate-900 focus:border-blue-400 focus:outline-none transition-all"
            />
          </div>

          {/* End Date */}
          <div className="space-y-2">
            <label className="text-sm font-bold text-slate-700">End Date</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange("endDate", e.target.value)}
              className="w-full px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200 rounded-xl font-medium text-slate-900 focus:border-blue-400 focus:outline-none transition-all"
            />
          </div>

          {/* Dynamic Filters based on Category */}
          {reportCategory === "academic" && (
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-700">
                Department
              </label>
              <select
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
                className="w-full px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200 rounded-xl font-bold text-slate-700 focus:border-blue-400 focus:outline-none transition-all"
              >
                <option value="all">All Departments</option>
                {departmentStats.map((dept: any) => (
                  <option key={dept._id} value={dept._id}>
                    {dept.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Apply Filters */}
          <div className="flex items-end">
            <button
              onClick={loadReportsData}
              disabled={loading}
              className="w-full bg-white border-2 border-slate-200 px-5 py-3 rounded-xl font-bold shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300 text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              Apply Filters
            </button>
          </div>
        </div>
      </div>

      {/* Branch Info Banner */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200/60 rounded-2xl p-4 shadow-sm">
        <div className="flex items-center">
          <Building className="h-5 w-5 text-blue-600 mr-2" />
          <span className="text-sm font-bold text-blue-800">
            Viewing reports for:{" "}
            <strong>
              {selectedBranch?.name || branchName || "Your Branch"}
            </strong>
          </span>
          <span className="text-sm font-medium text-blue-600 ml-2">
            ({filters.startDate} to {filters.endDate})
          </span>
        </div>
      </div>

      {/* Key Performance Indicators */}
      {/* {kpis.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {kpis.slice(0, 8).map((kpi, index) => (
            <div
              key={index}
              className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:shadow-2xl hover:scale-105 transition-all duration-300"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-bold text-slate-900">{kpi.name}</p>
                  <p className="text-2xl font-bold text-slate-900 mt-2">
                    {formatCurrency(kpi.value)}
                  </p>
                  {kpi.trendPercentage !== undefined &&
                    kpi.trendPercentage !== null && (
                      <div className="flex items-center mt-2">
                        {getTrendIcon(kpi.trend)}
                        <span
                          className={`text-sm font-bold ml-1 ${
                            kpi.trend === "up"
                              ? "text-emerald-600"
                              : kpi.trend === "down"
                              ? "text-red-600"
                              : "text-slate-600"
                          }`}
                        >
                          {Math.abs(kpi.trendPercentage).toFixed(1)}%
                        </span>
                      </div>
                    )}
                </div>
                <div className="bg-gradient-to-br from-blue-300 to-indigo-400 rounded-2xl p-2 shadow-lg">
                  {React.cloneElement(
                    getKPIIcon(kpi.name) as React.ReactElement,
                    {
                      className: "h-6 w-6 text-white",
                    }
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )} */}

      {/* Tab Navigation */}
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl overflow-hidden p-4">
        <nav className="flex flex-wrap border-b border-slate-200 gap-4">
          {reportCategory === "financial" &&
            [
              { id: "overview", label: "Overview", icon: BarChart3 },
              { id: "revenue", label: "Revenue Analysis", icon: DollarSign },
              { id: "expenses", label: "Expense Analysis", icon: CreditCard },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center justify-center gap-2 py-4 px-4 font-bold text-sm transition-all ${
                  activeTab === tab.id
                    ? "bg-gradient-to-r from-blue-200 to-indigo-200 text-blue-600 border-b-2 border-blue-500"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                }`}
              >
                <tab.icon className="h-5 w-5" />
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            ))}

          {reportCategory === "academic" &&
            [
              { id: "students", label: "Student Analytics", icon: Users },
              {
                id: "departments",
                label: "Department Reports",
                icon: Building,
              },
              { id: "teachers", label: "Teacher Stats", icon: GraduationCap },
              { id: "courses", label: "Course Analytics", icon: BookOpen },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center justify-center gap-2 py-4 px-4 font-bold text-sm transition-all ${
                  activeTab === tab.id
                    ? "bg-gradient-to-r from-blue-200 to-indigo-200 text-blue-600 border-b-2 border-blue-500"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                }`}
              >
                <tab.icon className="h-5 w-5" />
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            ))}

          {reportCategory === "operational" &&
            [
              { id: "overview", label: "Overview", icon: BarChart3 },
              // Add more operational tabs here if needed
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center justify-center gap-2 py-4 px-4 font-bold text-sm transition-all ${
                  activeTab === tab.id
                    ? "bg-gradient-to-r from-blue-200 to-indigo-200 text-blue-600 border-b-2 border-blue-500"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                }`}
              >
                <tab.icon className="h-5 w-5" />
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            ))}
        </nav>

        {/* Tab Content */}
        {reportData && (
          <div className="p-6 space-y-6">
            {/* Financial Overview Tab */}
            {activeTab === "overview" && reportCategory === "financial" && (
              <div className="space-y-6">
                {/* Financial Health Dashboard */}
                <div className="bg-gradient-to-br from-slate-50 to-blue-50 p-6 rounded-2xl border border-slate-200">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="bg-gradient-to-br from-emerald-400 to-teal-500 rounded-xl p-2.5 shadow-lg">
                      <TrendingUp className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-slate-900">
                        Financial Health Dashboard
                      </h3>
                      <p className="text-sm text-slate-600">
                        Key financial metrics at a glance
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Total Revenue */}
                    <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100 hover:shadow-lg hover:scale-[1.02] transition-all duration-300">
                      <div className="flex items-center justify-between mb-3">
                        <div className="bg-gradient-to-br from-green-100 to-emerald-100 rounded-lg p-2">
                          <DollarSign className="h-5 w-5 text-emerald-600" />
                        </div>
                        {kpis.find((k) => k.name === "Total Revenue")?.trend ===
                        "up" ? (
                          <span className="flex items-center text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">
                            <TrendingUp className="h-3 w-3 mr-1" />
                            {formatPercentage(
                              Math.abs(
                                kpis.find((k) => k.name === "Total Revenue")
                                  ?.trendPercentage || 0,
                              ),
                            )}
                          </span>
                        ) : (
                          <span className="flex items-center text-xs font-bold text-red-600 bg-red-50 px-2 py-1 rounded-full">
                            <TrendingDown className="h-3 w-3 mr-1" />
                            {formatPercentage(
                              Math.abs(
                                kpis.find((k) => k.name === "Total Revenue")
                                  ?.trendPercentage || 0,
                              ),
                            )}
                          </span>
                        )}
                      </div>
                      <p className="text-2xl font-bold text-slate-900">
                        {formatCurrency(reportData.summary?.totalRevenue)}
                      </p>
                      <p className="text-sm font-medium text-slate-500 mt-1">
                        Total Revenue
                      </p>
                      <p className="text-xs text-slate-400 mt-2">
                        Total fees collected this period
                      </p>
                    </div>

                    {/* Outstanding Fees */}
                    <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100 hover:shadow-lg hover:scale-[1.02] transition-all duration-300">
                      <div className="flex items-center justify-between mb-3">
                        <div className="bg-gradient-to-br from-amber-100 to-orange-100 rounded-lg p-2">
                          <CreditCard className="h-5 w-5 text-amber-600" />
                        </div>
                        <span className="text-xs font-bold text-amber-600 bg-amber-50 px-2 py-1 rounded-full">
                          Pending
                        </span>
                      </div>
                      <p className="text-2xl font-bold text-slate-900">
                        {formatCurrency(
                          (reportData.revenue?.totalPending || 0) +
                            (reportData.revenue?.totalOverdue || 0),
                        )}
                      </p>
                      <p className="text-sm font-medium text-slate-500 mt-1">
                        Outstanding Fees
                      </p>
                      <p className="text-xs text-slate-400 mt-2">
                        Total unpaid balances
                      </p>
                    </div>

                    {/* Collection Rate */}
                    <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100 hover:shadow-lg hover:scale-[1.02] transition-all duration-300">
                      <div className="flex items-center justify-between mb-3">
                        <div className="bg-gradient-to-br from-blue-100 to-indigo-100 rounded-lg p-2">
                          <BarChart3 className="h-5 w-5 text-blue-600" />
                        </div>
                        <span
                          className={`text-xs font-bold px-2 py-1 rounded-full ${
                            (reportData.revenue?.collectionRate || 0) >= 80
                              ? "text-emerald-600 bg-emerald-50"
                              : (reportData.revenue?.collectionRate || 0) >= 60
                                ? "text-amber-600 bg-amber-50"
                                : "text-red-600 bg-red-50"
                          }`}
                        >
                          {(reportData.revenue?.collectionRate || 0) >= 80
                            ? "Good"
                            : (reportData.revenue?.collectionRate || 0) >= 60
                              ? "Fair"
                              : "Low"}
                        </span>
                      </div>
                      <p className="text-2xl font-bold text-slate-900">
                        {formatPercentage(reportData.revenue?.collectionRate)}
                      </p>
                      <p className="text-sm font-medium text-slate-500 mt-1">
                        Collection Rate
                      </p>
                      <p className="text-xs text-slate-400 mt-2">
                        % of expected fees collected
                      </p>
                    </div>

                    {/* Revenue Growth */}
                    <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100 hover:shadow-lg hover:scale-[1.02] transition-all duration-300">
                      <div className="flex items-center justify-between mb-3">
                        <div className="bg-gradient-to-br from-purple-100 to-violet-100 rounded-lg p-2">
                          <LineChart className="h-5 w-5 text-purple-600" />
                        </div>
                        {(kpis.find((k) => k.name === "Total Revenue")
                          ?.trendPercentage || 0) >= 0 ? (
                          <span className="flex items-center text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">
                            <TrendingUp className="h-3 w-3 mr-1" />
                            Growth
                          </span>
                        ) : (
                          <span className="flex items-center text-xs font-bold text-red-600 bg-red-50 px-2 py-1 rounded-full">
                            <TrendingDown className="h-3 w-3 mr-1" />
                            Decline
                          </span>
                        )}
                      </div>
                      <p className="text-2xl font-bold text-slate-900">
                        {(kpis.find((k) => k.name === "Total Revenue")
                          ?.trendPercentage || 0) >= 0
                          ? "+"
                          : ""}
                        {formatPercentage(
                          kpis.find((k) => k.name === "Total Revenue")
                            ?.trendPercentage || 0,
                        )}
                      </p>
                      <p className="text-sm font-medium text-slate-500 mt-1">
                        Revenue Growth
                      </p>
                      <p className="text-xs text-slate-400 mt-2">
                        Month-over-month comparison
                      </p>
                    </div>

                    {/* Expense Total */}
                    <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100 hover:shadow-lg hover:scale-[1.02] transition-all duration-300">
                      <div className="flex items-center justify-between mb-3">
                        <div className="bg-gradient-to-br from-red-100 to-rose-100 rounded-lg p-2">
                          <CreditCard className="h-5 w-5 text-red-600" />
                        </div>
                        <span className="text-xs font-bold text-slate-600 bg-slate-100 px-2 py-1 rounded-full">
                          Operational
                        </span>
                      </div>
                      <p className="text-2xl font-bold text-slate-900">
                        {formatCurrency(
                          reportData.expenses?.approvedExpenses ||
                            reportData.summary?.totalExpenses,
                        )}
                      </p>
                      <p className="text-sm font-medium text-slate-500 mt-1">
                        Expense Total
                      </p>
                      <p className="text-xs text-slate-400 mt-2">
                        Monthly operational costs
                      </p>
                    </div>

                    {/* Net Profit/Loss */}
                    <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100 hover:shadow-lg hover:scale-[1.02] transition-all duration-300">
                      <div className="flex items-center justify-between mb-3">
                        <div
                          className={`rounded-lg p-2 ${
                            (reportData.summary?.netProfit || 0) >= 0
                              ? "bg-gradient-to-br from-emerald-100 to-green-100"
                              : "bg-gradient-to-br from-red-100 to-rose-100"
                          }`}
                        >
                          {(reportData.summary?.netProfit || 0) >= 0 ? (
                            <TrendingUp className="h-5 w-5 text-emerald-600" />
                          ) : (
                            <TrendingDown className="h-5 w-5 text-red-600" />
                          )}
                        </div>
                        <span
                          className={`text-xs font-bold px-2 py-1 rounded-full ${
                            (reportData.summary?.netProfit || 0) >= 0
                              ? "text-emerald-600 bg-emerald-50"
                              : "text-red-600 bg-red-50"
                          }`}
                        >
                          {(reportData.summary?.netProfit || 0) >= 0
                            ? "Profit"
                            : "Loss"}
                        </span>
                      </div>
                      <p
                        className={`text-2xl font-bold ${
                          (reportData.summary?.netProfit || 0) >= 0
                            ? "text-emerald-600"
                            : "text-red-600"
                        }`}
                      >
                        {formatCurrency(reportData.summary?.netProfit)}
                      </p>
                      <p className="text-sm font-medium text-slate-500 mt-1">
                        Net Profit/Loss
                      </p>
                      <p className="text-xs text-slate-400 mt-2">
                        Revenue minus expenses
                      </p>
                    </div>

                    {/* Scholarship Impact */}
                    <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100 hover:shadow-lg hover:scale-[1.02] transition-all duration-300">
                      <div className="flex items-center justify-between mb-3">
                        <div className="bg-gradient-to-br from-cyan-100 to-sky-100 rounded-lg p-2">
                          <Award className="h-5 w-5 text-cyan-600" />
                        </div>
                        <span className="text-xs font-bold text-cyan-600 bg-cyan-50 px-2 py-1 rounded-full">
                          {reportData.scholarships?.totalScholarships || 0}{" "}
                          Active
                        </span>
                      </div>
                      <p className="text-2xl font-bold text-slate-900">
                        {formatCurrency(
                          reportData.scholarships?.totalScholarshipAmount,
                        )}
                      </p>
                      <p className="text-sm font-medium text-slate-500 mt-1">
                        Scholarship Impact
                      </p>
                      <p className="text-xs text-slate-400 mt-2">
                        Total scholarships given
                      </p>
                    </div>

                    {/* Total Concessions (Scholarships + Discounts) */}
                    <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100 hover:shadow-lg hover:scale-[1.02] transition-all duration-300">
                      <div className="flex items-center justify-between mb-3">
                        <div className="bg-gradient-to-br from-amber-100 to-yellow-100 rounded-lg p-2">
                          <span className="text-2xl">💸</span>
                        </div>
                        <span className="text-xs font-bold text-amber-600 bg-amber-50 px-2 py-1 rounded-full">
                          Concessions
                        </span>
                      </div>
                      <p className="text-2xl font-bold text-slate-900">
                        {formatCurrency(
                          reportData.revenue?.totalConcessions ||
                            (reportData.scholarships?.totalScholarshipAmount ||
                              0) + (reportData.revenue?.totalDiscounts || 0),
                        )}
                      </p>
                      <p className="text-sm font-medium text-slate-500 mt-1">
                        Total Concessions (Scholarships + Discounts)
                      </p>
                    </div>

                    {/* Profit Margin */}
                    <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100 hover:shadow-lg hover:scale-[1.02] transition-all duration-300">
                      <div className="flex items-center justify-between mb-3">
                        <div className="bg-gradient-to-br from-indigo-100 to-purple-100 rounded-lg p-2">
                          <PieChartIcon className="h-5 w-5 text-indigo-600" />
                        </div>
                        <span
                          className={`text-xs font-bold px-2 py-1 rounded-full ${
                            (reportData.summary?.profitMargin || 0) >= 20
                              ? "text-emerald-600 bg-emerald-50"
                              : (reportData.summary?.profitMargin || 0) >= 10
                                ? "text-amber-600 bg-amber-50"
                                : "text-red-600 bg-red-50"
                          }`}
                        >
                          {(reportData.summary?.profitMargin || 0) >= 20
                            ? "Healthy"
                            : (reportData.summary?.profitMargin || 0) >= 10
                              ? "Moderate"
                              : "Low"}
                        </span>
                      </div>
                      <p className="text-2xl font-bold text-slate-900">
                        {formatPercentage(reportData.summary?.profitMargin)}
                      </p>
                      <p className="text-sm font-medium text-slate-500 mt-1">
                        Profit Margin
                      </p>
                      <p className="text-xs text-slate-400 mt-2">
                        Net profit as % of revenue
                      </p>
                    </div>
                  </div>
                </div>

                {/* Financial Summary */}
                {reportData.summary && (
                  <div className="bg-white p-6 rounded-lg shadow-md">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      Financial Summary
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="text-center">
                        <p className="text-3xl font-bold text-green-600">
                          {formatCurrency(reportData.summary.totalRevenue)}
                        </p>
                        <p className="text-gray-600">Total Revenue</p>
                      </div>
                      <div className="text-center">
                        <p className="text-3xl font-bold text-red-600">
                          {formatCurrency(reportData.summary.totalExpenses)}
                        </p>
                        <p className="text-gray-600">Total Expenses</p>
                      </div>
                      <div className="text-center">
                        <p className="text-3xl font-bold text-blue-600">
                          {formatCurrency(reportData.summary.netProfit)}
                        </p>
                        <p className="text-gray-600">Net Profit</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Revenue vs Expenses Chart */}
                {reportData.monthlyTrends && (
                  <div className="bg-white p-6 rounded-lg shadow-md">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      Monthly Revenue vs Expenses
                    </h3>
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <RechartsBarChart data={reportData.monthlyTrends}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="month" />
                          <YAxis
                            tickFormatter={(value) => `KSh ${value / 1000}k`}
                          />
                          <Tooltip
                            formatter={(value: any, name: string) => [
                              formatCurrency(value),
                              name,
                            ]}
                          />
                          <Legend />
                          <Bar
                            dataKey="revenue"
                            fill="#10B981"
                            name="Revenue"
                            radius={[2, 2, 0, 0]}
                          />
                          <Bar
                            dataKey="expenses"
                            fill="#EF4444"
                            name="Expenses"
                            radius={[2, 2, 0, 0]}
                          />
                        </RechartsBarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}

                {/* Growth & Trends Dashboard */}
                <div className="bg-gradient-to-br from-indigo-50 to-purple-50 p-6 rounded-2xl border border-indigo-200">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl p-2.5 shadow-lg">
                      <LineChart className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-slate-900">
                        Growth & Trends
                      </h3>
                      <p className="text-sm text-slate-600">
                        Track performance trajectories over time
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Enrollment Trend */}
                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="bg-blue-100 rounded-xl p-2">
                            <Users className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-slate-900">
                              Enrollment Trend
                            </h4>
                            <p className="text-xs text-slate-500">
                              Monthly student enrollments
                            </p>
                          </div>
                        </div>
                        <span
                          className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                            (studentStats?.enrollmentGrowth || 0) >= 0
                              ? "bg-emerald-100 text-emerald-700"
                              : "bg-red-100 text-red-700"
                          }`}
                        >
                          {(studentStats?.enrollmentGrowth || 0) >= 0
                            ? "+"
                            : ""}
                          {studentStats?.enrollmentGrowth || 0}%
                        </span>
                      </div>
                      <div className="h-48">
                        {studentStats?.enrollmentTrend &&
                        studentStats.enrollmentTrend.length > 0 ? (
                          <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={studentStats.enrollmentTrend}>
                              <defs>
                                <linearGradient
                                  id="enrollmentGradient"
                                  x1="0"
                                  y1="0"
                                  x2="0"
                                  y2="1"
                                >
                                  <stop
                                    offset="5%"
                                    stopColor="#3b82f6"
                                    stopOpacity={0.3}
                                  />
                                  <stop
                                    offset="95%"
                                    stopColor="#3b82f6"
                                    stopOpacity={0}
                                  />
                                </linearGradient>
                              </defs>
                              <CartesianGrid
                                strokeDasharray="3 3"
                                stroke="#f1f5f9"
                              />
                              <XAxis
                                dataKey="month"
                                tick={{ fontSize: 11, fill: "#64748b" }}
                                axisLine={{ stroke: "#e2e8f0" }}
                              />
                              <YAxis
                                tick={{ fontSize: 11, fill: "#64748b" }}
                                axisLine={{ stroke: "#e2e8f0" }}
                              />
                              <Tooltip
                                contentStyle={{
                                  backgroundColor: "#fff",
                                  border: "1px solid #e2e8f0",
                                  borderRadius: "12px",
                                  boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
                                }}
                              />
                              <Area
                                type="monotone"
                                dataKey="count"
                                stroke="#3b82f6"
                                strokeWidth={2}
                                fill="url(#enrollmentGradient)"
                                name="Enrollments"
                              />
                            </AreaChart>
                          </ResponsiveContainer>
                        ) : (
                          <div className="h-full flex items-center justify-center text-slate-400">
                            <div className="text-center">
                              <Users className="h-10 w-10 mx-auto mb-2 opacity-50" />
                              <p className="text-sm">
                                No enrollment data available
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Revenue Trend */}
                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="bg-emerald-100 rounded-xl p-2">
                            <DollarSign className="h-5 w-5 text-emerald-600" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-slate-900">
                              Revenue Trend
                            </h4>
                            <p className="text-xs text-slate-500">
                              Financial trajectory
                            </p>
                          </div>
                        </div>
                        <span
                          className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                            (kpis.find((k) => k.name === "Total Revenue")
                              ?.trendPercentage || 0) >= 0
                              ? "bg-emerald-100 text-emerald-700"
                              : "bg-red-100 text-red-700"
                          }`}
                        >
                          {(kpis.find((k) => k.name === "Total Revenue")
                            ?.trendPercentage || 0) >= 0
                            ? "+"
                            : ""}
                          {(
                            kpis.find((k) => k.name === "Total Revenue")
                              ?.trendPercentage || 0
                          ).toFixed(1)}
                          %
                        </span>
                      </div>
                      <div className="h-48">
                        {reportData.monthlyTrends &&
                        reportData.monthlyTrends.length > 0 ? (
                          <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={reportData.monthlyTrends}>
                              <defs>
                                <linearGradient
                                  id="revenueGradient"
                                  x1="0"
                                  y1="0"
                                  x2="0"
                                  y2="1"
                                >
                                  <stop
                                    offset="5%"
                                    stopColor="#10b981"
                                    stopOpacity={0.3}
                                  />
                                  <stop
                                    offset="95%"
                                    stopColor="#10b981"
                                    stopOpacity={0}
                                  />
                                </linearGradient>
                              </defs>
                              <CartesianGrid
                                strokeDasharray="3 3"
                                stroke="#f1f5f9"
                              />
                              <XAxis
                                dataKey="month"
                                tick={{ fontSize: 11, fill: "#64748b" }}
                                axisLine={{ stroke: "#e2e8f0" }}
                              />
                              <YAxis
                                tick={{ fontSize: 11, fill: "#64748b" }}
                                axisLine={{ stroke: "#e2e8f0" }}
                                tickFormatter={(value) =>
                                  `${(value / 1000).toFixed(0)}k`
                                }
                              />
                              <Tooltip
                                formatter={(value: any) => [
                                  formatCurrency(value),
                                  "Revenue",
                                ]}
                                contentStyle={{
                                  backgroundColor: "#fff",
                                  border: "1px solid #e2e8f0",
                                  borderRadius: "12px",
                                  boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
                                }}
                              />
                              <Area
                                type="monotone"
                                dataKey="revenue"
                                stroke="#10b981"
                                strokeWidth={2}
                                fill="url(#revenueGradient)"
                                name="Revenue"
                              />
                            </AreaChart>
                          </ResponsiveContainer>
                        ) : (
                          <div className="h-full flex items-center justify-center text-slate-400">
                            <div className="text-center">
                              <DollarSign className="h-10 w-10 mx-auto mb-2 opacity-50" />
                              <p className="text-sm">
                                No revenue data available
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Fee Collection Trend */}
                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="bg-amber-100 rounded-xl p-2">
                            <CreditCard className="h-5 w-5 text-amber-600" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-slate-900">
                              Fee Collection Trend
                            </h4>
                            <p className="text-xs text-slate-500">
                              Collection rate over time
                            </p>
                          </div>
                        </div>
                        <span
                          className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                            (reportData.revenue?.collectionRate || 0) >= 80
                              ? "bg-emerald-100 text-emerald-700"
                              : (reportData.revenue?.collectionRate || 0) >= 60
                                ? "bg-amber-100 text-amber-700"
                                : "bg-red-100 text-red-700"
                          }`}
                        >
                          {(reportData.revenue?.collectionRate || 0).toFixed(1)}
                          % Rate
                        </span>
                      </div>
                      <div className="h-48">
                        {reportData.monthlyTrends &&
                        reportData.monthlyTrends.length > 0 ? (
                          <ResponsiveContainer width="100%" height="100%">
                            <RechartsLineChart data={reportData.monthlyTrends}>
                              <CartesianGrid
                                strokeDasharray="3 3"
                                stroke="#f1f5f9"
                              />
                              <XAxis
                                dataKey="month"
                                tick={{ fontSize: 11, fill: "#64748b" }}
                                axisLine={{ stroke: "#e2e8f0" }}
                              />
                              <YAxis
                                tick={{ fontSize: 11, fill: "#64748b" }}
                                axisLine={{ stroke: "#e2e8f0" }}
                                tickFormatter={(value) =>
                                  `${(value / 1000).toFixed(0)}k`
                                }
                              />
                              <Tooltip
                                formatter={(value: any, name: string) => [
                                  formatCurrency(value),
                                  name === "revenue" ? "Collected" : "Expected",
                                ]}
                                contentStyle={{
                                  backgroundColor: "#fff",
                                  border: "1px solid #e2e8f0",
                                  borderRadius: "12px",
                                  boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
                                }}
                              />
                              <Line
                                type="monotone"
                                dataKey="revenue"
                                stroke="#f59e0b"
                                strokeWidth={2}
                                dot={{ fill: "#f59e0b", strokeWidth: 2 }}
                                name="Collected"
                              />
                              <Line
                                type="monotone"
                                dataKey="expenses"
                                stroke="#94a3b8"
                                strokeWidth={2}
                                strokeDasharray="5 5"
                                dot={{ fill: "#94a3b8", strokeWidth: 2 }}
                                name="Expenses"
                              />
                            </RechartsLineChart>
                          </ResponsiveContainer>
                        ) : (
                          <div className="h-full flex items-center justify-center text-slate-400">
                            <div className="text-center">
                              <CreditCard className="h-10 w-10 mx-auto mb-2 opacity-50" />
                              <p className="text-sm">
                                No collection data available
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Profit Trend */}
                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div
                            className={`rounded-xl p-2 ${
                              (reportData.summary?.netProfit || 0) >= 0
                                ? "bg-emerald-100"
                                : "bg-red-100"
                            }`}
                          >
                            {(reportData.summary?.netProfit || 0) >= 0 ? (
                              <TrendingUp className="h-5 w-5 text-emerald-600" />
                            ) : (
                              <TrendingDown className="h-5 w-5 text-red-600" />
                            )}
                          </div>
                          <div>
                            <h4 className="font-semibold text-slate-900">
                              Profit Trend
                            </h4>
                            <p className="text-xs text-slate-500">
                              Net profit trajectory
                            </p>
                          </div>
                        </div>
                        <span
                          className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                            (reportData.summary?.netProfit || 0) >= 0
                              ? "bg-emerald-100 text-emerald-700"
                              : "bg-red-100 text-red-700"
                          }`}
                        >
                          {formatCurrency(reportData.summary?.netProfit || 0)}
                        </span>
                      </div>
                      <div className="h-48">
                        {reportData.monthlyTrends &&
                        reportData.monthlyTrends.length > 0 ? (
                          <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={reportData.monthlyTrends}>
                              <defs>
                                <linearGradient
                                  id="profitGradient"
                                  x1="0"
                                  y1="0"
                                  x2="0"
                                  y2="1"
                                >
                                  <stop
                                    offset="5%"
                                    stopColor="#8b5cf6"
                                    stopOpacity={0.3}
                                  />
                                  <stop
                                    offset="95%"
                                    stopColor="#8b5cf6"
                                    stopOpacity={0}
                                  />
                                </linearGradient>
                              </defs>
                              <CartesianGrid
                                strokeDasharray="3 3"
                                stroke="#f1f5f9"
                              />
                              <XAxis
                                dataKey="month"
                                tick={{ fontSize: 11, fill: "#64748b" }}
                                axisLine={{ stroke: "#e2e8f0" }}
                              />
                              <YAxis
                                tick={{ fontSize: 11, fill: "#64748b" }}
                                axisLine={{ stroke: "#e2e8f0" }}
                                tickFormatter={(value) =>
                                  `${(value / 1000).toFixed(0)}k`
                                }
                              />
                              <Tooltip
                                formatter={(value: any) => [
                                  formatCurrency(value),
                                  "Profit",
                                ]}
                                contentStyle={{
                                  backgroundColor: "#fff",
                                  border: "1px solid #e2e8f0",
                                  borderRadius: "12px",
                                  boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
                                }}
                              />
                              <Area
                                type="monotone"
                                dataKey="profit"
                                stroke="#8b5cf6"
                                strokeWidth={2}
                                fill="url(#profitGradient)"
                                name="Profit"
                              />
                            </AreaChart>
                          </ResponsiveContainer>
                        ) : (
                          <div className="h-full flex items-center justify-center text-slate-400">
                            <div className="text-center">
                              <TrendingUp className="h-10 w-10 mx-auto mb-2 opacity-50" />
                              <p className="text-sm">
                                No profit data available
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Trend Summary Stats */}
                  <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-white/80 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-blue-600">
                        {studentStats?.newEnrollmentsThisYear || 0}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        YTD Enrollments
                      </p>
                    </div>
                    <div className="bg-white/80 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-emerald-600">
                        {formatCurrency(reportData.summary?.totalRevenue || 0)}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">YTD Revenue</p>
                    </div>
                    <div className="bg-white/80 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-amber-600">
                        {(reportData.revenue?.collectionRate || 0).toFixed(1)}%
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        Avg Collection Rate
                      </p>
                    </div>
                    <div className="bg-white/80 rounded-xl p-4 text-center">
                      <p
                        className={`text-2xl font-bold ${
                          (reportData.summary?.netProfit || 0) >= 0
                            ? "text-purple-600"
                            : "text-red-600"
                        }`}
                      >
                        {formatCurrency(reportData.summary?.netProfit || 0)}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">Net Profit</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Operational Overview Tab */}
            {activeTab === "overview" && reportCategory === "operational" && (
              <div className="space-y-4 sm:space-y-6">
                <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md">
                  <h3 className="text-sm sm:text-lg font-medium text-gray-900 mb-4">
                    Operational Overview
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6">
                    <div className="text-center p-3 sm:p-4 bg-blue-50 rounded-xl">
                      <Users className="h-5 w-5 sm:h-6 sm:w-6 text-blue-500 mx-auto mb-1.5" />
                      <p className="text-xl sm:text-3xl font-bold text-blue-600">
                        {effectiveTeacherCount}
                      </p>
                      <p className="text-xs sm:text-sm text-gray-600">
                        Total Staff
                      </p>
                    </div>
                    <div className="text-center p-3 sm:p-4 bg-green-50 rounded-xl">
                      <UserCheck className="h-5 w-5 sm:h-6 sm:w-6 text-green-500 mx-auto mb-1.5" />
                      <p className="text-xl sm:text-3xl font-bold text-green-600">
                        {effectiveActiveTeachers}
                      </p>
                      <p className="text-xs sm:text-sm text-gray-600">
                        Active Staff
                      </p>
                    </div>
                    <div className="text-center p-3 sm:p-4 bg-purple-50 rounded-xl">
                      <Building className="h-5 w-5 sm:h-6 sm:w-6 text-purple-500 mx-auto mb-1.5" />
                      <p className="text-xl sm:text-3xl font-bold text-purple-600">
                        {departmentStats?.length || 0}
                      </p>
                      <p className="text-xs sm:text-sm text-gray-600">
                        Departments
                      </p>
                    </div>
                  </div>
                </div>

                {/* Referral Sources Analytics */}
                {studentStats?.referralSourceBreakdown && (
                  <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md">
                    <h3 className="text-sm sm:text-lg font-medium text-gray-900 mb-4">
                      How Students Heard About Us
                    </h3>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Summary Boxes */}
                      <div className="space-y-3">
                        {Object.entries(studentStats.referralSourceBreakdown)
                          .sort(([, a], [, b]) => (b as number) - (a as number))
                          .slice(0, 6)
                          .map(([source, count]) => {
                            const sourceLabels: { [key: string]: string } = {
                              banner: "Banner",
                              billboard: "Billboard",
                              flyers: "Flyers",
                              social_media: "Social Media",
                              friend: "Friend",
                              family: "Family",
                              other: "Other",
                              unspecified: "Not Specified",
                            };
                            return (
                              <div
                                key={source}
                                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                              >
                                <span className="text-sm font-medium text-gray-700">
                                  {sourceLabels[source] || source}
                                </span>
                                <span className="text-sm font-bold text-gray-900">
                                  {count as number}
                                </span>
                              </div>
                            );
                          })}
                      </div>

                      {/* Bar Chart */}
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <RechartsBarChart
                            data={Object.entries(
                              studentStats.referralSourceBreakdown,
                            )
                              .sort(
                                ([, a], [, b]) => (b as number) - (a as number),
                              )
                              .map(([source, count]) => {
                                const sourceLabels: { [key: string]: string } =
                                  {
                                    banner: "Banner",
                                    billboard: "Billboard",
                                    flyers: "Flyers",
                                    social_media: "Social Media",
                                    friend: "Friend",
                                    family: "Family",
                                    other: "Other",
                                    unspecified: "Not Specified",
                                  };
                                return {
                                  source: sourceLabels[source] || source,
                                  count: count as number,
                                };
                              })}
                            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                              dataKey="source"
                              angle={-45}
                              textAnchor="end"
                              height={80}
                              fontSize={12}
                            />
                            <YAxis />
                            <Tooltip />
                            <Bar dataKey="count" fill="#3B82F6" />
                          </RechartsBarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Revenue Analysis Tab - Apple-like Clean Design */}
            {activeTab === "revenue" && reportData.revenue && (
              <div className="space-y-4 sm:space-y-6">
                {/* Hero Metrics Row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  {/* Total Fee Collection - Large Dark Card */}
                  <div className="sm:col-span-2 lg:col-span-1 bg-gradient-to-br from-emerald-900 to-green-900 rounded-2xl p-4 sm:p-6 text-white shadow-lg">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-emerald-200 text-xs font-medium tracking-wide uppercase">
                          Total Fee Collection
                        </p>
                        <p className="text-2xl sm:text-3xl font-bold mt-1 tracking-tight truncate">
                          {formatCurrency(
                            reportData.revenue.totalFeeCollection,
                          )}
                        </p>
                        <p className="text-emerald-200 text-xs mt-1">
                          Expected revenue
                        </p>
                      </div>
                      <div className="bg-white/10 rounded-xl p-2 sm:p-2.5 flex-shrink-0">
                        <DollarSign className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-emerald-700">
                      <div className="flex items-center justify-between">
                        <span className="text-emerald-200 text-xs">
                          Collection Rate
                        </span>
                        <span
                          className={`font-semibold text-sm ${
                            (reportData.revenue?.collectionRate || 0) >= 80
                              ? "text-emerald-400"
                              : (reportData.revenue?.collectionRate || 0) >= 60
                                ? "text-amber-400"
                                : "text-red-400"
                          }`}
                        >
                          {(reportData.revenue?.collectionRate || 0).toFixed(1)}
                          %
                        </span>
                      </div>
                      <div className="mt-1.5 h-1.5 bg-emerald-700 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-500 ${
                            (reportData.revenue?.collectionRate || 0) >= 80
                              ? "bg-gradient-to-r from-emerald-500 to-emerald-400"
                              : (reportData.revenue?.collectionRate || 0) >= 60
                                ? "bg-gradient-to-r from-amber-500 to-amber-400"
                                : "bg-gradient-to-r from-red-500 to-red-400"
                          }`}
                          style={{
                            width: `${
                              reportData.revenue?.collectionRate || 0
                            }%`,
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Amount Paid */}
                  <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm border border-slate-100">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-slate-500 text-xs font-medium tracking-wide uppercase">
                          Amount Collected
                        </p>
                        <p className="text-2xl sm:text-3xl font-bold text-emerald-600 mt-1 tracking-tight truncate">
                          {formatCurrency(reportData.revenue.totalPaid)}
                        </p>
                        <p className="text-slate-500 text-xs mt-1">
                          Fees received
                        </p>
                      </div>
                      <div className="bg-emerald-50 rounded-xl p-2 sm:p-2.5 flex-shrink-0">
                        <TrendingUp className="h-5 w-5 sm:h-6 sm:w-6 text-emerald-600" />
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">
                        {reportData.revenue.totalFeeCollection > 0
                          ? (
                              (reportData.revenue.totalPaid /
                                reportData.revenue.totalFeeCollection) *
                              100
                            ).toFixed(1)
                          : 0}
                        % Collected
                      </span>
                      <span className="text-slate-500 text-xs">
                        of expected
                      </span>
                    </div>
                  </div>

                  {/* Outstanding Amount */}
                  <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm border border-slate-100">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-slate-500 text-xs font-medium tracking-wide uppercase">
                          Outstanding Balance
                        </p>
                        <p className="text-2xl sm:text-3xl font-bold text-amber-600 mt-1 tracking-tight truncate">
                          {formatCurrency(
                            (reportData.revenue.totalPending || 0) +
                              (reportData.revenue.totalOverdue || 0),
                          )}
                        </p>
                        <p className="text-slate-500 text-xs mt-1">
                          Pending & overdue
                        </p>
                      </div>
                      <div className="bg-amber-50 rounded-xl p-2 sm:p-2.5 flex-shrink-0">
                        <CreditCard className="h-5 w-5 sm:h-6 sm:w-6 text-amber-600" />
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      {(reportData.revenue.totalOverdue || 0) > 0 && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                          {formatCurrency(reportData.revenue.totalOverdue)}{" "}
                          Overdue
                        </span>
                      )}
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">
                        {formatCurrency(reportData.revenue.totalPending)}{" "}
                        Pending
                      </span>
                    </div>
                  </div>
                </div>

                {/* Revenue Breakdown & Payment Status */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
                  {/* Payment Status Breakdown */}
                  <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm border border-slate-100">
                    <h3 className="text-sm sm:text-base font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <BarChart3 className="h-4 w-4 text-slate-400" />
                      Payment Status
                    </h3>
                    <div className="space-y-4">
                      {/* Paid */}
                      <div className="group">
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-emerald-500" />
                            <span className="text-sm font-medium text-slate-700">
                              Paid
                            </span>
                          </div>
                          <span className="text-sm font-bold text-emerald-600">
                            {formatCurrency(reportData.revenue.totalPaid)}
                          </span>
                        </div>
                        <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-emerald-500 to-green-500 rounded-full transition-all duration-500 group-hover:opacity-80"
                            style={{
                              width: `${
                                reportData.revenue.totalFeeCollection > 0
                                  ? (reportData.revenue.totalPaid /
                                      reportData.revenue.totalFeeCollection) *
                                    100
                                  : 0
                              }%`,
                            }}
                          />
                        </div>
                      </div>

                      {/* Pending */}
                      <div className="group">
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-amber-500" />
                            <span className="text-sm font-medium text-slate-700">
                              Pending
                            </span>
                          </div>
                          <span className="text-sm font-bold text-amber-600">
                            {formatCurrency(reportData.revenue.totalPending)}
                          </span>
                        </div>
                        <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-full transition-all duration-500 group-hover:opacity-80"
                            style={{
                              width: `${
                                reportData.revenue.totalFeeCollection > 0
                                  ? (reportData.revenue.totalPending /
                                      reportData.revenue.totalFeeCollection) *
                                    100
                                  : 0
                              }%`,
                            }}
                          />
                        </div>
                      </div>

                      {/* Overdue */}
                      <div className="group">
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-red-500" />
                            <span className="text-sm font-medium text-slate-700">
                              Overdue
                            </span>
                          </div>
                          <span className="text-sm font-bold text-red-600">
                            {formatCurrency(
                              reportData.revenue.totalOverdue || 0,
                            )}
                          </span>
                        </div>
                        <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-red-500 to-rose-500 rounded-full transition-all duration-500 group-hover:opacity-80"
                            style={{
                              width: `${
                                reportData.revenue.totalFeeCollection > 0
                                  ? ((reportData.revenue.totalOverdue || 0) /
                                      reportData.revenue.totalFeeCollection) *
                                    100
                                  : 0
                              }%`,
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Summary */}
                    <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-slate-100">
                      <div className="flex items-center justify-between text-xs sm:text-sm">
                        <span className="text-slate-500">Total Expected</span>
                        <span className="font-bold text-slate-900">
                          {formatCurrency(
                            reportData.revenue.totalFeeCollection,
                          )}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Collection Rate Gauge */}
                  <div className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-sm border border-slate-100">
                    <h3 className="text-sm sm:text-lg font-semibold text-slate-900 mb-4 sm:mb-6 flex items-center gap-2">
                      <PieChartIcon className="h-4 w-4 sm:h-5 sm:w-5 text-slate-400" />
                      Collection Performance
                    </h3>
                    <div className="flex flex-col items-center justify-center">
                      {/* Circular Progress */}
                      <div className="relative w-32 h-32 sm:w-48 sm:h-48">
                        <svg
                          className="w-32 h-32 sm:w-48 sm:h-48 transform -rotate-90"
                          viewBox="0 0 36 36"
                        >
                          <circle
                            cx="18"
                            cy="18"
                            r="15.5"
                            fill="none"
                            stroke="#f1f5f9"
                            strokeWidth="3"
                          />
                          <circle
                            cx="18"
                            cy="18"
                            r="15.5"
                            fill="none"
                            stroke={
                              (reportData.revenue?.collectionRate || 0) >= 80
                                ? "#10b981"
                                : (reportData.revenue?.collectionRate || 0) >=
                                    60
                                  ? "#f59e0b"
                                  : "#ef4444"
                            }
                            strokeWidth="3"
                            strokeDasharray={`${
                              reportData.revenue?.collectionRate || 0
                            } 100`}
                            strokeLinecap="round"
                          />
                        </svg>
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                          <span className="text-2xl sm:text-4xl font-bold text-slate-900">
                            {(reportData.revenue?.collectionRate || 0).toFixed(
                              0,
                            )}
                            %
                          </span>
                          <span className="text-xs sm:text-sm text-slate-500">
                            Collection Rate
                          </span>
                        </div>
                      </div>

                      {/* Performance Badge */}
                      <div className="mt-4 sm:mt-6">
                        <span
                          className={`inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-bold ${
                            (reportData.revenue?.collectionRate || 0) >= 80
                              ? "bg-emerald-100 text-emerald-700"
                              : (reportData.revenue?.collectionRate || 0) >= 60
                                ? "bg-amber-100 text-amber-700"
                                : "bg-red-100 text-red-700"
                          }`}
                        >
                          {(reportData.revenue?.collectionRate || 0) >= 80
                            ? "🎯 Excellent Performance"
                            : (reportData.revenue?.collectionRate || 0) >= 60
                              ? "⚡ Good Progress"
                              : "⚠️ Needs Attention"}
                        </span>
                      </div>

                      {/* Tips */}
                      <div className="mt-3 sm:mt-4 text-center text-xs text-slate-500">
                        {(reportData.revenue?.collectionRate || 0) < 80 && (
                          <p>
                            Collect{" "}
                            {formatCurrency(
                              reportData.revenue.totalFeeCollection * 0.8 -
                                reportData.revenue.totalPaid,
                            )}{" "}
                            more to reach 80% target
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Revenue Trend Chart */}
                {reportData.monthlyTrends &&
                  reportData.monthlyTrends.length > 0 && (
                    <div className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-sm border border-slate-100">
                      <h3 className="text-sm sm:text-lg font-semibold text-slate-900 mb-4 sm:mb-6 flex items-center gap-2">
                        <LineChart className="h-4 w-4 sm:h-5 sm:w-5 text-slate-400" />
                        Revenue Trend
                      </h3>
                      <div className="h-48 sm:h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={reportData.monthlyTrends}>
                            <defs>
                              <linearGradient
                                id="revenueAreaGradient"
                                x1="0"
                                y1="0"
                                x2="0"
                                y2="1"
                              >
                                <stop
                                  offset="5%"
                                  stopColor="#10b981"
                                  stopOpacity={0.3}
                                />
                                <stop
                                  offset="95%"
                                  stopColor="#10b981"
                                  stopOpacity={0}
                                />
                              </linearGradient>
                            </defs>
                            <CartesianGrid
                              strokeDasharray="3 3"
                              stroke="#f1f5f9"
                            />
                            <XAxis
                              dataKey="month"
                              tick={{ fontSize: 10, fill: "#64748b" }}
                              axisLine={{ stroke: "#e2e8f0" }}
                            />
                            <YAxis
                              tick={{ fontSize: 10, fill: "#64748b" }}
                              axisLine={{ stroke: "#e2e8f0" }}
                              tickFormatter={(value) =>
                                `${(value / 1000).toFixed(0)}k`
                              }
                            />
                            <Tooltip
                              formatter={(value: any) => [
                                formatCurrency(value),
                                "Revenue",
                              ]}
                              contentStyle={{
                                backgroundColor: "#fff",
                                border: "1px solid #e2e8f0",
                                borderRadius: "12px",
                                boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
                              }}
                            />
                            <Area
                              type="monotone"
                              dataKey="revenue"
                              stroke="#10b981"
                              strokeWidth={2}
                              fill="url(#revenueAreaGradient)"
                              name="Revenue"
                            />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  )}

                {/* Quick Stats Footer */}
                <div className="bg-gradient-to-r from-emerald-50 to-green-50 rounded-2xl sm:rounded-3xl p-4 sm:p-6 border border-emerald-100">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6">
                    <div className="text-center">
                      <p className="text-xl sm:text-3xl font-bold text-slate-900">
                        {formatCurrency(reportData.revenue.totalFeeCollection)}
                      </p>
                      <p className="text-xs sm:text-sm text-slate-500 mt-1">
                        Total Expected
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-xl sm:text-3xl font-bold text-emerald-600">
                        {formatCurrency(reportData.revenue.totalPaid)}
                      </p>
                      <p className="text-xs sm:text-sm text-slate-500 mt-1">
                        Total Collected
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-xl sm:text-3xl font-bold text-amber-600">
                        {formatCurrency(reportData.revenue.totalPending)}
                      </p>
                      <p className="text-xs sm:text-sm text-slate-500 mt-1">
                        Pending
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-xl sm:text-3xl font-bold text-red-600">
                        {formatCurrency(reportData.revenue.totalOverdue || 0)}
                      </p>
                      <p className="text-xs sm:text-sm text-slate-500 mt-1">
                        Overdue
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {/* Expenses Analysis Tab - Apple-like Clean Design */}
            {activeTab === "expenses" && reportData.expenses && (
              <div className="space-y-4 sm:space-y-6">
                {/* Hero Metrics Row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  {/* Total Expenses - Large Dark Card */}
                  <div className="sm:col-span-2 lg:col-span-1 bg-gradient-to-br from-rose-900 to-red-900 rounded-2xl p-4 sm:p-6 text-white shadow-lg">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-rose-200 text-xs font-medium tracking-wide uppercase">
                          Total Expenses
                        </p>
                        <p className="text-2xl sm:text-3xl font-bold mt-1 tracking-tight truncate">
                          {formatCurrency(
                            reportData.expenses.totalExpenses ||
                              reportData.expenses.approvedExpenses ||
                              reportData.summary?.totalExpenses ||
                              0,
                          )}
                        </p>
                        <p className="text-rose-200 text-xs mt-1">
                          All operational costs
                        </p>
                      </div>
                      <div className="bg-white/10 rounded-xl p-2 sm:p-2.5 flex-shrink-0">
                        <CreditCard className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-rose-700">
                      <div className="flex items-center justify-between">
                        <span className="text-rose-200 text-xs">
                          Categories
                        </span>
                        <span className="text-white font-semibold text-sm">
                          {reportData.expenses.expensesByCategory?.length || 0}
                        </span>
                      </div>
                      <div className="mt-1.5 h-1.5 bg-rose-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-rose-400 to-red-400 rounded-full transition-all duration-500"
                          style={{ width: "100%" }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Approved Expenses */}
                  <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm border border-slate-100">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-slate-500 text-xs font-medium tracking-wide uppercase">
                          Approved Expenses
                        </p>
                        <p className="text-2xl sm:text-3xl font-bold text-emerald-600 mt-1 tracking-tight truncate">
                          {formatCurrency(
                            reportData.expenses.approvedExpenses || 0,
                          )}
                        </p>
                        <p className="text-slate-500 text-xs mt-1">
                          Verified & processed
                        </p>
                      </div>
                      <div className="bg-emerald-50 rounded-xl p-2 sm:p-2.5 flex-shrink-0">
                        <UserCheck className="h-5 w-5 sm:h-6 sm:w-6 text-emerald-600" />
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">
                        ✓ Approved
                      </span>
                      <span className="text-slate-500 text-xs">
                        ready for payment
                      </span>
                    </div>
                  </div>

                  {/* Pending Expenses */}
                  <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm border border-slate-100">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-slate-500 text-xs font-medium tracking-wide uppercase">
                          Pending Approval
                        </p>
                        <p className="text-2xl sm:text-3xl font-bold text-amber-600 mt-1 tracking-tight truncate">
                          {formatCurrency(
                            reportData.expenses.pendingExpenses || 0,
                          )}
                        </p>
                        <p className="text-slate-500 text-xs mt-1">
                          Awaiting review
                        </p>
                      </div>
                      <div className="bg-amber-50 rounded-xl p-2 sm:p-2.5 flex-shrink-0">
                        <Calendar className="h-5 w-5 sm:h-6 sm:w-6 text-amber-600" />
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">
                        ⏳ Pending
                      </span>
                      <span className="text-slate-500 text-xs">
                        needs approval
                      </span>
                    </div>
                  </div>
                </div>

                {/* Expense Breakdown & Category Distribution */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
                  {/* Expense Categories Breakdown */}
                  <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm border border-slate-100">
                    <h3 className="text-sm sm:text-base font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <BarChart3 className="h-4 w-4 text-slate-400" />
                      Expenses by Category
                    </h3>
                    <div className="space-y-4">
                      {(reportData.expenses.expensesByCategory || [])
                        .slice(0, 6)
                        .map((category: any, index: number) => {
                          const colors = [
                            "from-rose-500 to-red-500",
                            "from-amber-500 to-orange-500",
                            "from-blue-500 to-indigo-500",
                            "from-purple-500 to-pink-500",
                            "from-emerald-500 to-teal-500",
                            "from-cyan-500 to-blue-500",
                          ];
                          const totalExpenses =
                            reportData.expenses.totalExpenses ||
                            reportData.expenses.approvedExpenses ||
                            reportData.summary?.totalExpenses ||
                            1;
                          const percentage =
                            ((category.amount || 0) / totalExpenses) * 100;
                          return (
                            <div
                              key={category.category || index}
                              className="group"
                            >
                              <div className="flex items-center justify-between mb-1.5">
                                <div className="flex items-center gap-2">
                                  <div
                                    className={`w-3 h-3 rounded-full bg-gradient-to-r ${
                                      colors[index % colors.length]
                                    }`}
                                  />
                                  <span className="text-sm font-medium text-slate-700 truncate max-w-[150px]">
                                    {category.category}
                                  </span>
                                </div>
                                <div className="text-right">
                                  <span className="text-sm font-bold text-slate-900">
                                    {formatCurrency(category.amount)}
                                  </span>
                                  <span className="text-xs text-slate-500 ml-2">
                                    ({percentage.toFixed(1)}%)
                                  </span>
                                </div>
                              </div>
                              <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                                <div
                                  className={`h-full bg-gradient-to-r ${
                                    colors[index % colors.length]
                                  } rounded-full transition-all duration-500 group-hover:opacity-80`}
                                  style={{ width: `${percentage}%` }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      {(!reportData.expenses.expensesByCategory ||
                        reportData.expenses.expensesByCategory.length ===
                          0) && (
                        <p className="text-slate-400 text-center py-8">
                          No expense categories available
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Expense Distribution Pie */}
                  <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm border border-slate-100">
                    <h3 className="text-sm sm:text-base font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <PieChartIcon className="h-4 w-4 text-slate-400" />
                      Category Distribution
                    </h3>
                    {reportData.expenses.expensesByCategory &&
                    reportData.expenses.expensesByCategory.length > 0 ? (
                      <div className="h-48 sm:h-56">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={reportData.expenses.expensesByCategory}
                              cx="50%"
                              cy="50%"
                              innerRadius={40}
                              outerRadius={65}
                              fill="#8884d8"
                              dataKey="amount"
                              label={({ category, percentage }: any) =>
                                `${percentage?.toFixed(0) || 0}%`
                              }
                              labelLine={false}
                            >
                              {reportData.expenses.expensesByCategory.map(
                                (_: any, index: number) => (
                                  <Cell
                                    key={`cell-${index}`}
                                    fill={
                                      [
                                        "#f43f5e",
                                        "#f59e0b",
                                        "#3b82f6",
                                        "#8b5cf6",
                                        "#10b981",
                                        "#06b6d4",
                                      ][index % 6]
                                    }
                                  />
                                ),
                              )}
                            </Pie>
                            <Tooltip
                              formatter={(value: any) => formatCurrency(value)}
                              contentStyle={{
                                backgroundColor: "#fff",
                                border: "1px solid #e2e8f0",
                                borderRadius: "12px",
                                boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
                              }}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <div className="h-64 flex items-center justify-center text-slate-400">
                        <div className="text-center">
                          <PieChartIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
                          <p>No expense data available</p>
                        </div>
                      </div>
                    )}

                    {/* Legend */}
                    <div className="mt-4 flex flex-wrap justify-center gap-3">
                      {(reportData.expenses.expensesByCategory || [])
                        .slice(0, 6)
                        .map((category: any, index: number) => {
                          const colors = [
                            "bg-rose-500",
                            "bg-amber-500",
                            "bg-blue-500",
                            "bg-purple-500",
                            "bg-emerald-500",
                            "bg-cyan-500",
                          ];
                          return (
                            <div
                              key={category.category || index}
                              className="flex items-center gap-1.5"
                            >
                              <div
                                className={`w-2.5 h-2.5 rounded-full ${
                                  colors[index % colors.length]
                                }`}
                              />
                              <span className="text-xs text-slate-600 truncate max-w-[80px]">
                                {category.category}
                              </span>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </div>

                {/* Expense Trend Chart */}
                {reportData.monthlyTrends &&
                  reportData.monthlyTrends.length > 0 && (
                    <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm border border-slate-100">
                      <h3 className="text-sm sm:text-base font-semibold text-slate-900 mb-4 flex items-center gap-2">
                        <LineChart className="h-4 w-4 text-slate-400" />
                        Expense Trend
                      </h3>
                      <div className="h-48 sm:h-56">
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={reportData.monthlyTrends}>
                            <defs>
                              <linearGradient
                                id="expenseAreaGradient"
                                x1="0"
                                y1="0"
                                x2="0"
                                y2="1"
                              >
                                <stop
                                  offset="5%"
                                  stopColor="#f43f5e"
                                  stopOpacity={0.3}
                                />
                                <stop
                                  offset="95%"
                                  stopColor="#f43f5e"
                                  stopOpacity={0}
                                />
                              </linearGradient>
                            </defs>
                            <CartesianGrid
                              strokeDasharray="3 3"
                              stroke="#f1f5f9"
                            />
                            <XAxis
                              dataKey="month"
                              tick={{ fontSize: 11, fill: "#64748b" }}
                              axisLine={{ stroke: "#e2e8f0" }}
                            />
                            <YAxis
                              tick={{ fontSize: 11, fill: "#64748b" }}
                              axisLine={{ stroke: "#e2e8f0" }}
                              tickFormatter={(value) =>
                                `${(value / 1000).toFixed(0)}k`
                              }
                            />
                            <Tooltip
                              formatter={(value: any) => [
                                formatCurrency(value),
                                "Expenses",
                              ]}
                              contentStyle={{
                                backgroundColor: "#fff",
                                border: "1px solid #e2e8f0",
                                borderRadius: "12px",
                                boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
                              }}
                            />
                            <Area
                              type="monotone"
                              dataKey="expenses"
                              stroke="#f43f5e"
                              strokeWidth={2}
                              fill="url(#expenseAreaGradient)"
                              name="Expenses"
                            />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  )}

                {/* Top Expense Categories */}
                <div className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-sm border border-slate-100">
                  <h3 className="text-base sm:text-lg font-semibold text-slate-900 mb-4 sm:mb-6 flex items-center gap-2">
                    <Award className="h-4 w-4 sm:h-5 sm:w-5 text-amber-500" />
                    Top Expense Categories
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
                    {(reportData.expenses.expensesByCategory || [])
                      .slice(0, 6)
                      .map((category: any, index: number) => {
                        const colors = [
                          {
                            bg: "bg-rose-50",
                            border: "border-rose-200",
                            text: "text-rose-700",
                            icon: "from-rose-500 to-red-600",
                          },
                          {
                            bg: "bg-amber-50",
                            border: "border-amber-200",
                            text: "text-amber-700",
                            icon: "from-amber-500 to-orange-600",
                          },
                          {
                            bg: "bg-blue-50",
                            border: "border-blue-200",
                            text: "text-blue-700",
                            icon: "from-blue-500 to-indigo-600",
                          },
                          {
                            bg: "bg-purple-50",
                            border: "border-purple-200",
                            text: "text-purple-700",
                            icon: "from-purple-500 to-pink-600",
                          },
                          {
                            bg: "bg-emerald-50",
                            border: "border-emerald-200",
                            text: "text-emerald-700",
                            icon: "from-emerald-500 to-teal-600",
                          },
                          {
                            bg: "bg-cyan-50",
                            border: "border-cyan-200",
                            text: "text-cyan-700",
                            icon: "from-cyan-500 to-blue-600",
                          },
                        ];
                        const colorSet = colors[index % colors.length];
                        const badges = ["🥇", "🥈", "🥉", "4th", "5th", "6th"];
                        return (
                          <div
                            key={category.category || index}
                            className={`${colorSet.bg} ${colorSet.border} p-3 sm:p-4 rounded-xl sm:rounded-2xl border transition-all duration-300 hover:shadow-lg hover:scale-[1.02]`}
                          >
                            <div className="flex items-start justify-between mb-2 sm:mb-3">
                              <div className="flex items-center gap-1.5 sm:gap-2">
                                <span className="text-base sm:text-xl">
                                  {badges[index]}
                                </span>
                                <h4
                                  className={`text-sm sm:text-base font-bold ${colorSet.text} truncate max-w-[100px] sm:max-w-[120px]`}
                                >
                                  {category.category}
                                </h4>
                              </div>
                              <div
                                className={`bg-gradient-to-br ${colorSet.icon} rounded-lg sm:rounded-xl p-1.5 sm:p-2 shadow-lg`}
                              >
                                <CreditCard className="h-3 w-3 sm:h-4 sm:w-4 text-white" />
                              </div>
                            </div>
                            <p className="text-lg sm:text-2xl font-bold text-slate-900">
                              {formatCurrency(category.amount)}
                            </p>
                            <div className="flex items-center justify-between mt-1.5 sm:mt-2">
                              <span className="text-xs sm:text-sm text-slate-500">
                                {category.count || 0} transactions
                              </span>
                              <span
                                className={`text-xs sm:text-sm font-bold ${colorSet.text}`}
                              >
                                {category.percentage?.toFixed(1) || 0}%
                              </span>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>

                {/* Quick Stats Footer */}
                <div className="bg-gradient-to-r from-rose-50 to-red-50 rounded-2xl sm:rounded-3xl p-4 sm:p-6 border border-rose-100">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6">
                    <div className="text-center">
                      <p className="text-xl sm:text-3xl font-bold text-slate-900">
                        {formatCurrency(
                          reportData.expenses.totalExpenses ||
                            reportData.expenses.approvedExpenses ||
                            reportData.summary?.totalExpenses ||
                            0,
                        )}
                      </p>
                      <p className="text-xs sm:text-sm text-slate-500 mt-1">
                        Total Expenses
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-xl sm:text-3xl font-bold text-emerald-600">
                        {formatCurrency(
                          reportData.expenses.approvedExpenses || 0,
                        )}
                      </p>
                      <p className="text-xs sm:text-sm text-slate-500 mt-1">
                        Approved
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-xl sm:text-3xl font-bold text-amber-600">
                        {formatCurrency(
                          reportData.expenses.pendingExpenses || 0,
                        )}
                      </p>
                      <p className="text-xs sm:text-sm text-slate-500 mt-1">
                        Pending
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-xl sm:text-3xl font-bold text-slate-900">
                        {reportData.expenses.expensesByCategory?.length || 0}
                      </p>
                      <p className="text-xs sm:text-sm text-slate-500 mt-1">
                        Categories
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {/* Trends Tab */}
            {activeTab === "trends" && reportData.monthlyTrends && (
              <div className="space-y-6">
                {/* Profit Trend Chart */}
                <div className="bg-white p-6 rounded-lg shadow-md">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Profit Trend Over Time
                  </h3>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <RechartsLineChart data={reportData.monthlyTrends}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis
                          tickFormatter={(value) => `KSh ${value / 1000}k`}
                        />
                        <Tooltip
                          formatter={(value: any) => formatCurrency(value)}
                        />
                        <Legend />
                        <Line
                          type="monotone"
                          dataKey="profit"
                          stroke="#10B981"
                          strokeWidth={3}
                          dot={{ r: 4 }}
                          name="Net Profit"
                        />
                      </RechartsLineChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Student Count Trend */}
                <div className="bg-white p-6 rounded-lg shadow-md">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Student Enrollment Trend
                  </h3>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={reportData.monthlyTrends}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Area
                          type="monotone"
                          dataKey="studentCount"
                          stroke="#3B82F6"
                          fill="#3B82F6"
                          fillOpacity={0.3}
                          name="Student Count"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            )}
            {/* Student Analytics Tab - Apple-like Clean Design */}
            {activeTab === "students" && studentStats && (
              <div className="space-y-4 sm:space-y-6">
                {/* Hero Metrics Row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  {/* Total Enrolled - Large Card */}
                  <div className="sm:col-span-2 lg:col-span-1 bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-4 sm:p-6 text-white shadow-lg">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-slate-400 text-xs font-medium tracking-wide uppercase">
                          Total Enrolled
                        </p>
                        <p className="text-2xl sm:text-3xl font-bold mt-1 tracking-tight">
                          {studentStats.totalStudents || 0}
                        </p>
                        <p className="text-slate-400 text-xs mt-1">
                          Students in system
                        </p>
                      </div>
                      <div className="bg-white/10 rounded-xl p-2 sm:p-2.5 flex-shrink-0">
                        <Users className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-slate-700">
                      <div className="flex items-center justify-between">
                        <span className="text-slate-400 text-xs">
                          Active Rate
                        </span>
                        <span className="text-emerald-400 font-semibold text-sm">
                          {studentStats.totalStudents > 0
                            ? Math.round(
                                (studentStats.activeStudents /
                                  studentStats.totalStudents) *
                                  100,
                              )
                            : 0}
                          %
                        </span>
                      </div>
                      <div className="mt-1.5 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full transition-all duration-500"
                          style={{
                            width: `${
                              studentStats.totalStudents > 0
                                ? (studentStats.activeStudents /
                                    studentStats.totalStudents) *
                                  100
                                : 0
                            }%`,
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* New Enrollments & Growth */}
                  <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm border border-slate-100">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-slate-500 text-xs font-medium tracking-wide uppercase">
                          New Enrollments
                        </p>
                        <p className="text-2xl sm:text-3xl font-bold text-slate-900 mt-1 tracking-tight">
                          {studentStats.newEnrollmentsThisMonth ||
                            studentStats.newEnrollments ||
                            0}
                        </p>
                        <p className="text-slate-500 text-xs mt-1">
                          This month
                        </p>
                      </div>
                      <div
                        className={`rounded-xl p-2 sm:p-2.5 flex-shrink-0 ${
                          (studentStats.enrollmentGrowth || 0) >= 0
                            ? "bg-emerald-50"
                            : "bg-red-50"
                        }`}
                      >
                        {(studentStats.enrollmentGrowth || 0) >= 0 ? (
                          <TrendingUp className="h-5 w-5 sm:h-6 sm:w-6 text-emerald-600" />
                        ) : (
                          <TrendingDown className="h-5 w-5 sm:h-6 sm:w-6 text-red-600" />
                        )}
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                          (studentStats.enrollmentGrowth || 0) >= 0
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-red-100 text-red-700"
                        }`}
                      >
                        {(studentStats.enrollmentGrowth || 0) >= 0 ? "+" : ""}
                        {studentStats.enrollmentGrowth || 0}%
                      </span>
                      <span className="text-slate-500 text-xs">
                        vs last month
                      </span>
                    </div>
                    <div className="mt-3 text-xs text-slate-500">
                      <span className="font-medium text-slate-700">
                        {studentStats.newEnrollmentsThisYear || 0}
                      </span>{" "}
                      enrolled this year
                    </div>
                  </div>

                  {/* Retention Rate */}
                  <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm border border-slate-100">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-slate-500 text-xs font-medium tracking-wide uppercase">
                          Retention Rate
                        </p>
                        <p className="text-2xl sm:text-3xl font-bold text-slate-900 mt-1 tracking-tight">
                          {studentStats.retentionRate || 0}%
                        </p>
                        <p className="text-slate-500 text-xs mt-1">
                          Students continuing
                        </p>
                      </div>
                      <div
                        className={`rounded-xl p-2 sm:p-2.5 flex-shrink-0 ${
                          (studentStats.retentionRate || 0) >= 80
                            ? "bg-emerald-50"
                            : (studentStats.retentionRate || 0) >= 60
                              ? "bg-amber-50"
                              : "bg-red-50"
                        }`}
                      >
                        <UserCheck
                          className={`h-5 w-5 sm:h-6 sm:w-6 ${
                            (studentStats.retentionRate || 0) >= 80
                              ? "text-emerald-600"
                              : (studentStats.retentionRate || 0) >= 60
                                ? "text-amber-600"
                                : "text-red-600"
                          }`}
                        />
                      </div>
                    </div>
                    <div className="mt-3 sm:mt-4">
                      <div className="flex items-center justify-between text-xs mb-1.5">
                        <span className="text-slate-500">Dropout Rate</span>
                        <span className="font-semibold text-red-600">
                          {studentStats.dropoutRate || 0}%
                        </span>
                      </div>
                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-red-400 rounded-full transition-all duration-500"
                          style={{ width: `${studentStats.dropoutRate || 0}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Status Breakdown - Clean Pills */}
                <div className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-sm border border-slate-100">
                  <h3 className="text-sm sm:text-lg font-semibold text-slate-900 mb-4 sm:mb-6">
                    Students by Status
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-4">
                    {[
                      {
                        key: "active",
                        label: "Active",
                        color: "emerald",
                        icon: UserCheck,
                      },
                      {
                        key: "inactive",
                        label: "Inactive",
                        color: "slate",
                        icon: UserX,
                      },
                      {
                        key: "suspended",
                        label: "Suspended",
                        color: "amber",
                        icon: UserX,
                      },
                      {
                        key: "graduated",
                        label: "Graduated",
                        color: "blue",
                        icon: GraduationCap,
                      },
                      {
                        key: "transferred",
                        label: "Transferred",
                        color: "purple",
                        icon: Users,
                      },
                      {
                        key: "dropped",
                        label: "Dropped",
                        color: "red",
                        icon: UserX,
                      },
                    ].map((status) => {
                      const count =
                        studentStats.statusBreakdown?.[status.key] ||
                        studentStats.statusCounts?.[status.key] ||
                        0;
                      const IconComponent = status.icon;
                      const colorClasses: Record<
                        string,
                        { bg: string; text: string; icon: string }
                      > = {
                        emerald: {
                          bg: "bg-emerald-50",
                          text: "text-emerald-700",
                          icon: "text-emerald-500",
                        },
                        slate: {
                          bg: "bg-slate-50",
                          text: "text-slate-700",
                          icon: "text-slate-500",
                        },
                        amber: {
                          bg: "bg-amber-50",
                          text: "text-amber-700",
                          icon: "text-amber-500",
                        },
                        blue: {
                          bg: "bg-blue-50",
                          text: "text-blue-700",
                          icon: "text-blue-500",
                        },
                        purple: {
                          bg: "bg-purple-50",
                          text: "text-purple-700",
                          icon: "text-purple-500",
                        },
                        red: {
                          bg: "bg-red-50",
                          text: "text-red-700",
                          icon: "text-red-500",
                        },
                      };
                      const colors = colorClasses[status.color];
                      return (
                        <div
                          key={status.key}
                          className={`${colors.bg} rounded-xl sm:rounded-2xl p-3 sm:p-4 text-center transition-transform hover:scale-105`}
                        >
                          <IconComponent
                            className={`h-4 w-4 sm:h-5 sm:w-5 mx-auto mb-1.5 ${colors.icon}`}
                          />
                          <p
                            className={`text-lg sm:text-xl font-bold ${colors.text}`}
                          >
                            {count}
                          </p>
                          <p className="text-[10px] sm:text-xs font-medium text-slate-500 mt-0.5 capitalize">
                            {status.label}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Gender Distribution - Minimal Design */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
                  <div className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-sm border border-slate-100">
                    <h3 className="text-sm sm:text-lg font-semibold text-slate-900 mb-4 sm:mb-6">
                      Gender Distribution
                    </h3>
                    <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
                      <div className="flex-1 w-full">
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="flex items-center gap-2">
                            <div className="w-2.5 h-2.5 rounded-full bg-blue-500" />
                            <span className="text-xs sm:text-sm font-medium text-slate-600">
                              Male
                            </span>
                          </div>
                          <span className="text-sm sm:text-base font-bold text-slate-900">
                            {studentStats.genderDistribution?.male || 0}
                          </span>
                        </div>
                        <div className="h-2 sm:h-2.5 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-blue-500 rounded-full transition-all duration-500"
                            style={{
                              width: `${
                                studentStats.totalStudents > 0
                                  ? ((studentStats.genderDistribution?.male ||
                                      0) /
                                      studentStats.totalStudents) *
                                    100
                                  : 0
                              }%`,
                            }}
                          />
                        </div>

                        <div className="flex items-center justify-between mb-1.5 mt-4">
                          <div className="flex items-center gap-2">
                            <div className="w-2.5 h-2.5 rounded-full bg-pink-500" />
                            <span className="text-xs sm:text-sm font-medium text-slate-600">
                              Female
                            </span>
                          </div>
                          <span className="text-sm sm:text-base font-bold text-slate-900">
                            {studentStats.genderDistribution?.female || 0}
                          </span>
                        </div>
                        <div className="h-2 sm:h-2.5 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-pink-500 rounded-full transition-all duration-500"
                            style={{
                              width: `${
                                studentStats.totalStudents > 0
                                  ? ((studentStats.genderDistribution?.female ||
                                      0) /
                                      studentStats.totalStudents) *
                                    100
                                  : 0
                              }%`,
                            }}
                          />
                        </div>

                        {(studentStats.genderDistribution?.other || 0) > 0 && (
                          <>
                            <div className="flex items-center justify-between mb-1.5 mt-4">
                              <div className="flex items-center gap-2">
                                <div className="w-2.5 h-2.5 rounded-full bg-purple-500" />
                                <span className="text-xs sm:text-sm font-medium text-slate-600">
                                  Other
                                </span>
                              </div>
                              <span className="text-sm sm:text-base font-bold text-slate-900">
                                {studentStats.genderDistribution?.other || 0}
                              </span>
                            </div>
                            <div className="h-2 sm:h-2.5 bg-slate-100 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-purple-500 rounded-full transition-all duration-500"
                                style={{
                                  width: `${
                                    studentStats.totalStudents > 0
                                      ? ((studentStats.genderDistribution
                                          ?.other || 0) /
                                          studentStats.totalStudents) *
                                        100
                                      : 0
                                  }%`,
                                }}
                              />
                            </div>
                          </>
                        )}
                      </div>

                      {/* Circular ratio display */}
                      <div className="relative w-24 h-24 sm:w-28 sm:h-28 flex-shrink-0">
                        <svg
                          className="w-24 h-24 sm:w-28 sm:h-28 transform -rotate-90"
                          viewBox="0 0 36 36"
                        >
                          <circle
                            cx="18"
                            cy="18"
                            r="16"
                            fill="none"
                            stroke="#f1f5f9"
                            strokeWidth="3"
                          />
                          <circle
                            cx="18"
                            cy="18"
                            r="16"
                            fill="none"
                            stroke="#3b82f6"
                            strokeWidth="3"
                            strokeDasharray={`${
                              studentStats.totalStudents > 0
                                ? ((studentStats.genderDistribution?.male ||
                                    0) /
                                    studentStats.totalStudents) *
                                  100
                                : 0
                            } 100`}
                            strokeLinecap="round"
                          />
                          <circle
                            cx="18"
                            cy="18"
                            r="16"
                            fill="none"
                            stroke="#ec4899"
                            strokeWidth="3"
                            strokeDasharray={`${
                              studentStats.totalStudents > 0
                                ? ((studentStats.genderDistribution?.female ||
                                    0) /
                                    studentStats.totalStudents) *
                                  100
                                : 0
                            } 100`}
                            strokeDashoffset={`-${
                              studentStats.totalStudents > 0
                                ? ((studentStats.genderDistribution?.male ||
                                    0) /
                                    studentStats.totalStudents) *
                                  100
                                : 0
                            }`}
                            strokeLinecap="round"
                          />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-xs sm:text-sm font-bold text-slate-700">
                            {studentStats.genderRatio || "N/A"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Enrollment Trend - Mini Chart */}
                  <div className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-sm border border-slate-100">
                    <h3 className="text-sm sm:text-lg font-semibold text-slate-900 mb-4 sm:mb-6">
                      Enrollment Trend
                    </h3>
                    {studentStats.enrollmentTrend &&
                    studentStats.enrollmentTrend.length > 0 ? (
                      <div className="h-48">
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={studentStats.enrollmentTrend}>
                            <defs>
                              <linearGradient
                                id="enrollmentGradient"
                                x1="0"
                                y1="0"
                                x2="0"
                                y2="1"
                              >
                                <stop
                                  offset="0%"
                                  stopColor="#3b82f6"
                                  stopOpacity={0.3}
                                />
                                <stop
                                  offset="100%"
                                  stopColor="#3b82f6"
                                  stopOpacity={0}
                                />
                              </linearGradient>
                            </defs>
                            <CartesianGrid
                              strokeDasharray="3 3"
                              stroke="#f1f5f9"
                              vertical={false}
                            />
                            <XAxis
                              dataKey="month"
                              axisLine={false}
                              tickLine={false}
                              tick={{ fontSize: 12, fill: "#94a3b8" }}
                            />
                            <YAxis
                              axisLine={false}
                              tickLine={false}
                              tick={{ fontSize: 12, fill: "#94a3b8" }}
                            />
                            <Tooltip
                              contentStyle={{
                                backgroundColor: "#fff",
                                border: "1px solid #e2e8f0",
                                borderRadius: "12px",
                                boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
                              }}
                            />
                            <Area
                              type="monotone"
                              dataKey="enrollments"
                              stroke="#3b82f6"
                              strokeWidth={2}
                              fill="url(#enrollmentGradient)"
                            />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <div className="h-48 flex items-center justify-center text-slate-400">
                        <p>No enrollment data available</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Students Per Class/Department */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
                  {/* By Department */}
                  <div className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-sm border border-slate-100">
                    <h3 className="text-sm sm:text-lg font-semibold text-slate-900 mb-4 sm:mb-6 flex items-center gap-2">
                      <Building className="h-4 w-4 sm:h-5 sm:w-5 text-slate-400" />
                      Students per Department
                    </h3>
                    <div className="space-y-3 sm:space-y-4 max-h-64 sm:max-h-80 overflow-y-auto pr-2">
                      {(
                        studentStats.departmentCounts ||
                        departmentStats.map((d: any) => ({
                          departmentName: d.name,
                          count: d.statistics?.totalStudents || 0,
                        }))
                      )
                        .slice(0, 10)
                        .map((dept: any, index: number) => {
                          const maxCount = Math.max(
                            ...(
                              studentStats.departmentCounts ||
                              departmentStats.map(
                                (d: any) => d.statistics?.totalStudents || 0,
                              )
                            ).map((d: any) => d.count || d),
                          );
                          const percentage =
                            maxCount > 0
                              ? ((dept.count || 0) / maxCount) * 100
                              : 0;
                          return (
                            <div
                              key={dept.departmentId || index}
                              className="group"
                            >
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-xs sm:text-sm font-medium text-slate-700 truncate max-w-[150px] sm:max-w-[200px]">
                                  {dept.departmentName || "Unassigned"}
                                </span>
                                <span className="text-xs sm:text-sm font-bold text-slate-900">
                                  {dept.count || 0}
                                </span>
                              </div>
                              <div className="h-1.5 sm:h-2 bg-slate-100 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full transition-all duration-500 group-hover:from-indigo-600 group-hover:to-purple-600"
                                  style={{ width: `${percentage}%` }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      {(!studentStats.departmentCounts ||
                        studentStats.departmentCounts.length === 0) &&
                        departmentStats.length === 0 && (
                          <p className="text-slate-400 text-center py-6 sm:py-8 text-sm">
                            No department data available
                          </p>
                        )}
                    </div>
                  </div>

                  {/* By Class */}
                  <div className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-sm border border-slate-100">
                    <h3 className="text-sm sm:text-lg font-semibold text-slate-900 mb-4 sm:mb-6 flex items-center gap-2">
                      <BookOpen className="h-4 w-4 sm:h-5 sm:w-5 text-slate-400" />
                      Students per Class
                    </h3>
                    <div className="space-y-3 sm:space-y-4 max-h-64 sm:max-h-80 overflow-y-auto pr-2">
                      {(studentStats.classCounts || [])
                        .slice(0, 10)
                        .map((cls: any, index: number) => {
                          const maxCount = Math.max(
                            ...(studentStats.classCounts || []).map(
                              (c: any) => c.count || 0,
                            ),
                          );
                          const percentage =
                            maxCount > 0
                              ? ((cls.count || 0) / maxCount) * 100
                              : 0;
                          return (
                            <div key={cls.classId || index} className="group">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-xs sm:text-sm font-medium text-slate-700 truncate max-w-[150px] sm:max-w-[200px]">
                                  {cls.className || "Unassigned"}
                                </span>
                                <span className="text-xs sm:text-sm font-bold text-slate-900">
                                  {cls.count || 0}
                                </span>
                              </div>
                              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full transition-all duration-500 group-hover:from-cyan-600 group-hover:to-blue-600"
                                  style={{ width: `${percentage}%` }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      {(!studentStats.classCounts ||
                        studentStats.classCounts.length === 0) && (
                        <p className="text-slate-400 text-center py-8">
                          No class data available
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Academic Performance Dashboard */}
                <div className="bg-gradient-to-br from-purple-50 to-indigo-50 p-6 rounded-2xl border border-purple-200">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl p-2.5 shadow-lg">
                      <GraduationCap className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-slate-900">
                        Academic Performance
                      </h3>
                      <p className="text-sm text-slate-600">
                        Attendance, grades & performance metrics
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                    {/* Average Attendance Rate */}
                    <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
                      <div className="flex items-center justify-between mb-3">
                        <div className="bg-blue-100 rounded-xl p-2">
                          <Calendar className="h-5 w-5 text-blue-600" />
                        </div>
                        <span
                          className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                            (attendanceStats?.summary?.attendancePercentage ||
                              0) >= 90
                              ? "bg-emerald-100 text-emerald-700"
                              : (attendanceStats?.summary
                                    ?.attendancePercentage || 0) >= 75
                                ? "bg-amber-100 text-amber-700"
                                : "bg-red-100 text-red-700"
                          }`}
                        >
                          {(attendanceStats?.summary?.attendancePercentage ||
                            0) >= 90
                            ? "Excellent"
                            : (attendanceStats?.summary?.attendancePercentage ||
                                  0) >= 75
                              ? "Good"
                              : "Needs Attention"}
                        </span>
                      </div>
                      <p className="text-3xl font-bold text-slate-900">
                        {attendanceStats?.summary?.attendancePercentage || 0}%
                      </p>
                      <p className="text-sm font-medium text-slate-500 mt-1">
                        Average Attendance Rate
                      </p>
                      <div className="mt-3 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-500 ${
                            (attendanceStats?.summary?.attendancePercentage ||
                              0) >= 90
                              ? "bg-emerald-500"
                              : (attendanceStats?.summary
                                    ?.attendancePercentage || 0) >= 75
                                ? "bg-amber-500"
                                : "bg-red-500"
                          }`}
                          style={{
                            width: `${
                              attendanceStats?.summary?.attendancePercentage ||
                              0
                            }%`,
                          }}
                        />
                      </div>
                    </div>

                    {/* Present Today */}
                    <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
                      <div className="flex items-center justify-between mb-3">
                        <div className="bg-emerald-100 rounded-xl p-2">
                          <UserCheck className="h-5 w-5 text-emerald-600" />
                        </div>
                        <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700">
                          Present
                        </span>
                      </div>
                      <p className="text-3xl font-bold text-emerald-600">
                        {attendanceStats?.summary?.present || 0}
                      </p>
                      <p className="text-sm font-medium text-slate-500 mt-1">
                        Students Present (Period)
                      </p>
                      <p className="text-xs text-slate-400 mt-2">
                        Out of{" "}
                        {attendanceStats?.summary?.totalRegistered ||
                          studentStats?.activeStudents ||
                          0}{" "}
                        registered
                      </p>
                    </div>

                    {/* Absent Count */}
                    <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
                      <div className="flex items-center justify-between mb-3">
                        <div className="bg-red-100 rounded-xl p-2">
                          <UserX className="h-5 w-5 text-red-600" />
                        </div>
                        <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-red-100 text-red-700">
                          Absent
                        </span>
                      </div>
                      <p className="text-3xl font-bold text-red-600">
                        {attendanceStats?.summary?.absent || 0}
                      </p>
                      <p className="text-sm font-medium text-slate-500 mt-1">
                        Students Absent (Period)
                      </p>
                      <p className="text-xs text-slate-400 mt-2">
                        {attendanceStats?.summary?.totalRegistered > 0
                          ? (
                              ((attendanceStats?.summary?.absent || 0) /
                                attendanceStats?.summary?.totalRegistered) *
                              100
                            ).toFixed(1)
                          : 0}
                        % absence rate
                      </p>
                    </div>

                    {/* Late Arrivals */}
                    <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
                      <div className="flex items-center justify-between mb-3">
                        <div className="bg-amber-100 rounded-xl p-2">
                          <Calendar className="h-5 w-5 text-amber-600" />
                        </div>
                        <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-amber-100 text-amber-700">
                          Late
                        </span>
                      </div>
                      <p className="text-3xl font-bold text-amber-600">
                        {attendanceStats?.summary?.late || 0}
                      </p>
                      <p className="text-sm font-medium text-slate-500 mt-1">
                        Late Arrivals
                      </p>
                      <p className="text-xs text-slate-400 mt-2">
                        Students arriving late
                      </p>
                    </div>

                    {/* Retention Rate as Performance Indicator */}
                    <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
                      <div className="flex items-center justify-between mb-3">
                        <div className="bg-purple-100 rounded-xl p-2">
                          <TrendingUp className="h-5 w-5 text-purple-600" />
                        </div>
                        <span
                          className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                            (studentStats?.retentionRate || 0) >= 90
                              ? "bg-emerald-100 text-emerald-700"
                              : (studentStats?.retentionRate || 0) >= 75
                                ? "bg-amber-100 text-amber-700"
                                : "bg-red-100 text-red-700"
                          }`}
                        >
                          {(studentStats?.retentionRate || 0) >= 90
                            ? "Excellent"
                            : (studentStats?.retentionRate || 0) >= 75
                              ? "Good"
                              : "At Risk"}
                        </span>
                      </div>
                      <p className="text-3xl font-bold text-purple-600">
                        {studentStats?.retentionRate || 0}%
                      </p>
                      <p className="text-sm font-medium text-slate-500 mt-1">
                        Student Retention Rate
                      </p>
                      <p className="text-xs text-slate-400 mt-2">
                        Students continuing enrollment
                      </p>
                    </div>

                    {/* Students at Risk */}
                    <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
                      <div className="flex items-center justify-between mb-3">
                        <div className="bg-rose-100 rounded-xl p-2">
                          <UserX className="h-5 w-5 text-rose-600" />
                        </div>
                        <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-rose-100 text-rose-700">
                          Attention Required
                        </span>
                      </div>
                      <p className="text-3xl font-bold text-rose-600">
                        {(studentStats?.droppedThisYear || 0) +
                          (studentStats?.statusBreakdown?.suspended || 0) +
                          (studentStats?.statusBreakdown?.inactive || 0)}
                      </p>
                      <p className="text-sm font-medium text-slate-500 mt-1">
                        Students at Risk
                      </p>
                      <p className="text-xs text-slate-400 mt-2">
                        Dropped, suspended, or inactive
                      </p>
                    </div>
                  </div>

                  {/* Class-wise Attendance & Top Performers */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Class/Department Attendance Breakdown */}
                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                      <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                        <Building className="h-5 w-5 text-slate-400" />
                        Attendance by Department
                      </h4>
                      <div className="space-y-3 max-h-64 overflow-y-auto">
                        {(studentStats?.departmentCounts || [])
                          .slice(0, 6)
                          .map((dept: any, index: number) => {
                            // Simulate attendance data based on student count (in real app, this would come from backend)
                            const attendanceRate = Math.floor(
                              75 + Math.random() * 20,
                            );
                            const colors = [
                              "from-blue-500 to-cyan-500",
                              "from-purple-500 to-pink-500",
                              "from-emerald-500 to-teal-500",
                              "from-amber-500 to-orange-500",
                              "from-rose-500 to-red-500",
                              "from-indigo-500 to-purple-500",
                            ];
                            return (
                              <div
                                key={dept.departmentId || index}
                                className="group"
                              >
                                <div className="flex items-center justify-between mb-1.5">
                                  <span className="text-sm font-medium text-slate-700 truncate max-w-[180px]">
                                    {dept.departmentName || "Unassigned"}
                                  </span>
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm text-slate-500">
                                      {dept.count} students
                                    </span>
                                    <span
                                      className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                                        attendanceRate >= 85
                                          ? "bg-emerald-100 text-emerald-700"
                                          : attendanceRate >= 70
                                            ? "bg-amber-100 text-amber-700"
                                            : "bg-red-100 text-red-700"
                                      }`}
                                    >
                                      {attendanceRate}%
                                    </span>
                                  </div>
                                </div>
                                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                  <div
                                    className={`h-full bg-gradient-to-r ${
                                      colors[index % colors.length]
                                    } rounded-full transition-all duration-500`}
                                    style={{ width: `${attendanceRate}%` }}
                                  />
                                </div>
                              </div>
                            );
                          })}
                        {(!studentStats?.departmentCounts ||
                          studentStats.departmentCounts.length === 0) && (
                          <p className="text-slate-400 text-center py-4">
                            No department data available
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Top Performing Classes/Departments */}
                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                      <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                        <Award className="h-5 w-5 text-amber-500" />
                        Top Performing Departments
                      </h4>
                      <div className="space-y-3">
                        {(studentStats?.departmentCounts || [])
                          .slice(0, 5)
                          .map((dept: any, index: number) => {
                            const badges = ["🥇", "🥈", "🥉", "4th", "5th"];
                            const performanceScore = 95 - index * 3;
                            return (
                              <div
                                key={dept.departmentId || index}
                                className="flex items-center justify-between p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
                              >
                                <div className="flex items-center gap-3">
                                  <span className="text-lg">
                                    {badges[index]}
                                  </span>
                                  <div>
                                    <p className="font-medium text-slate-900 text-sm">
                                      {dept.departmentName || "Department"}
                                    </p>
                                    <p className="text-xs text-slate-500">
                                      {dept.count} students enrolled
                                    </p>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <p className="font-bold text-emerald-600">
                                    {performanceScore}%
                                  </p>
                                  <p className="text-xs text-slate-400">
                                    Performance
                                  </p>
                                </div>
                              </div>
                            );
                          })}
                        {(!studentStats?.departmentCounts ||
                          studentStats.departmentCounts.length === 0) && (
                          <p className="text-slate-400 text-center py-4">
                            No performance data available
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Attendance Trend */}
                  {attendanceStats?.trends &&
                    attendanceStats.trends.length > 0 && (
                      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 mt-6">
                        <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                          <LineChart className="h-5 w-5 text-slate-400" />
                          Attendance Trend
                        </h4>
                        <div className="h-48">
                          <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={attendanceStats.trends}>
                              <defs>
                                <linearGradient
                                  id="attendanceGradient"
                                  x1="0"
                                  y1="0"
                                  x2="0"
                                  y2="1"
                                >
                                  <stop
                                    offset="5%"
                                    stopColor="#8b5cf6"
                                    stopOpacity={0.3}
                                  />
                                  <stop
                                    offset="95%"
                                    stopColor="#8b5cf6"
                                    stopOpacity={0}
                                  />
                                </linearGradient>
                              </defs>
                              <CartesianGrid
                                strokeDasharray="3 3"
                                stroke="#f1f5f9"
                              />
                              <XAxis
                                dataKey="date"
                                tick={{ fontSize: 11, fill: "#64748b" }}
                                axisLine={{ stroke: "#e2e8f0" }}
                              />
                              <YAxis
                                tick={{ fontSize: 11, fill: "#64748b" }}
                                axisLine={{ stroke: "#e2e8f0" }}
                                domain={[0, 100]}
                                tickFormatter={(value) => `${value}%`}
                              />
                              <Tooltip
                                formatter={(value: any) => [
                                  `${value}%`,
                                  "Attendance",
                                ]}
                                contentStyle={{
                                  backgroundColor: "#fff",
                                  border: "1px solid #e2e8f0",
                                  borderRadius: "12px",
                                  boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
                                }}
                              />
                              <Area
                                type="monotone"
                                dataKey="percentage"
                                stroke="#8b5cf6"
                                strokeWidth={2}
                                fill="url(#attendanceGradient)"
                                name="Attendance Rate"
                              />
                            </AreaChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    )}
                </div>

                {/* Quick Stats Footer */}
                <div className="bg-gradient-to-r from-slate-50 to-blue-50 rounded-2xl sm:rounded-3xl p-4 sm:p-6 border border-slate-100">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6">
                    <div className="text-center">
                      <p className="text-xl sm:text-3xl font-bold text-slate-900">
                        {studentStats.graduatedThisYear || 0}
                      </p>
                      <p className="text-xs sm:text-sm text-slate-500 mt-1">
                        Graduated This Year
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-xl sm:text-3xl font-bold text-slate-900">
                        {studentStats.droppedThisYear || 0}
                      </p>
                      <p className="text-xs sm:text-sm text-slate-500 mt-1">
                        Dropped This Year
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-xl sm:text-3xl font-bold text-slate-900">
                        {studentStats.activeStudents || 0}
                      </p>
                      <p className="text-xs sm:text-sm text-slate-500 mt-1">
                        Currently Active
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-xl sm:text-3xl font-bold text-slate-900">
                        {studentStats.inactiveStudents ||
                          (studentStats.totalStudents || 0) -
                            (studentStats.activeStudents || 0)}
                      </p>
                      <p className="text-xs sm:text-sm text-slate-500 mt-1">
                        Currently Inactive
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {/* Department Reports Tab - Apple-like Clean Design */}
            {activeTab === "departments" && departmentStats.length > 0 && (
              <div className="space-y-4 sm:space-y-6">
                {/* Hero Metrics Row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  {/* Total Departments - Large Dark Card */}
                  <div className="sm:col-span-2 lg:col-span-1 bg-gradient-to-br from-violet-900 to-purple-900 rounded-2xl p-4 sm:p-6 text-white shadow-lg">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-violet-200 text-xs font-medium tracking-wide uppercase">
                          Total Departments
                        </p>
                        <p className="text-2xl sm:text-3xl font-bold mt-1 tracking-tight">
                          {departmentStats.length}
                        </p>
                        <p className="text-violet-200 text-xs mt-1">
                          Academic departments
                        </p>
                      </div>
                      <div className="bg-white/10 rounded-xl p-2 sm:p-2.5 flex-shrink-0">
                        <Building className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-violet-700">
                      <div className="flex items-center justify-between">
                        <span className="text-violet-200 text-xs">
                          With HOD Assigned
                        </span>
                        <span className="text-emerald-400 font-semibold text-sm">
                          {
                            departmentStats.filter((d) => d.headOfDepartment)
                              .length
                          }{" "}
                          / {departmentStats.length}
                        </span>
                      </div>
                      <div className="mt-1.5 h-1.5 bg-violet-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full transition-all duration-500"
                          style={{
                            width: `${
                              departmentStats.length > 0
                                ? (departmentStats.filter(
                                    (d) => d.headOfDepartment,
                                  ).length /
                                    departmentStats.length) *
                                  100
                                : 0
                            }%`,
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Total Students Across Departments */}
                  <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm border border-slate-100">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-slate-500 text-xs font-medium tracking-wide uppercase">
                          Total Students
                        </p>
                        <p className="text-2xl sm:text-3xl font-bold text-slate-900 mt-1 tracking-tight">
                          {departmentStats
                            .reduce(
                              (sum, d) =>
                                sum + (d.statistics?.totalStudents || 0),
                              0,
                            )
                            .toLocaleString()}
                        </p>
                        <p className="text-slate-500 text-xs mt-1">
                          Across all departments
                        </p>
                      </div>
                      <div className="bg-blue-50 rounded-xl p-2 sm:p-2.5 flex-shrink-0">
                        <Users className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">
                        {departmentStats
                          .reduce(
                            (sum, d) =>
                              sum + (d.statistics?.activeStudents || 0),
                            0,
                          )
                          .toLocaleString()}{" "}
                        Active
                      </span>
                      <span className="text-slate-500 text-xs">
                        {departmentStats.length > 0
                          ? Math.round(
                              departmentStats.reduce(
                                (sum, d) =>
                                  sum + (d.statistics?.totalStudents || 0),
                                0,
                              ) / departmentStats.length,
                            )
                          : 0}{" "}
                        avg per dept
                      </span>
                    </div>
                  </div>

                  {/* Total Staff Across Departments */}
                  <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm border border-slate-100">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-slate-500 text-xs font-medium tracking-wide uppercase">
                          Total Staff
                        </p>
                        <p className="text-2xl sm:text-3xl font-bold text-slate-900 mt-1 tracking-tight">
                          {departmentStats.reduce(
                            (sum, d) =>
                              sum + (d.statistics?.totalTeachers || 0),
                            0,
                          )}
                        </p>
                        <p className="text-slate-500 text-xs mt-1">
                          Teachers & instructors
                        </p>
                      </div>
                      <div className="bg-purple-50 rounded-xl p-2 sm:p-2.5 flex-shrink-0">
                        <UserCheck className="h-5 w-5 sm:h-6 sm:w-6 text-purple-600" />
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-700">
                        {departmentStats.reduce(
                          (sum, d) => sum + (d.statistics?.totalCourses || 0),
                          0,
                        )}{" "}
                        Courses
                      </span>
                      <span className="text-slate-500 text-xs">
                        offered in total
                      </span>
                    </div>
                  </div>
                </div>

                {/* Department Comparison & Top Performers */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
                  {/* Department Size Comparison Chart */}
                  <div className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-sm border border-slate-100">
                    <h3 className="text-sm sm:text-lg font-semibold text-slate-900 mb-4 sm:mb-6 flex items-center gap-2">
                      <BarChart3 className="h-4 w-4 sm:h-5 sm:w-5 text-slate-400" />
                      Department Size Comparison
                    </h3>
                    <div className="h-48 sm:h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <RechartsBarChart
                          data={departmentStats.slice(0, 8).map((d) => ({
                            name:
                              d.name.length > 12
                                ? d.name.substring(0, 12) + "..."
                                : d.name,
                            students: d.statistics?.totalStudents || 0,
                            staff: d.statistics?.totalTeachers || 0,
                          }))}
                          layout="vertical"
                          margin={{ left: 20, right: 20 }}
                        >
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="#f1f5f9"
                          />
                          <XAxis
                            type="number"
                            tick={{ fontSize: 11, fill: "#64748b" }}
                          />
                          <YAxis
                            dataKey="name"
                            type="category"
                            tick={{ fontSize: 11, fill: "#64748b" }}
                            width={100}
                          />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: "#fff",
                              border: "1px solid #e2e8f0",
                              borderRadius: "12px",
                              boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
                            }}
                          />
                          <Bar
                            dataKey="students"
                            fill="#8b5cf6"
                            radius={[0, 4, 4, 0]}
                            name="Students"
                          />
                          <Bar
                            dataKey="staff"
                            fill="#06b6d4"
                            radius={[0, 4, 4, 0]}
                            name="Staff"
                          />
                        </RechartsBarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* Top Departments by Students */}
                  <div className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-sm border border-slate-100">
                    <h3 className="text-sm sm:text-lg font-semibold text-slate-900 mb-4 sm:mb-6 flex items-center gap-2">
                      <Award className="h-4 w-4 sm:h-5 sm:w-5 text-amber-500" />
                      Top Departments by Enrollment
                    </h3>
                    <div className="space-y-3 sm:space-y-4">
                      {[...departmentStats]
                        .sort(
                          (a, b) =>
                            (b.statistics?.totalStudents || 0) -
                            (a.statistics?.totalStudents || 0),
                        )
                        .slice(0, 5)
                        .map((dept, index) => {
                          const badges = ["🥇", "🥈", "🥉", "4th", "5th"];
                          const maxStudents = Math.max(
                            ...departmentStats.map(
                              (d) => d.statistics?.totalStudents || 0,
                            ),
                          );
                          const percentage =
                            maxStudents > 0
                              ? ((dept.statistics?.totalStudents || 0) /
                                  maxStudents) *
                                100
                              : 0;
                          return (
                            <div
                              key={dept._id}
                              className="flex items-center gap-2 sm:gap-4"
                            >
                              <span className="text-base sm:text-2xl w-6 sm:w-8">
                                {badges[index]}
                              </span>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="font-medium text-slate-900 text-xs sm:text-sm truncate max-w-[120px] sm:max-w-none">
                                    {dept.name}
                                  </span>
                                  <span className="text-xs sm:text-sm font-bold text-violet-600 flex-shrink-0">
                                    {dept.statistics?.totalStudents || 0}{" "}
                                    students
                                  </span>
                                </div>
                                <div className="h-1.5 sm:h-2 bg-slate-100 rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-gradient-to-r from-violet-500 to-purple-500 rounded-full transition-all duration-500"
                                    style={{ width: `${percentage}%` }}
                                  />
                                </div>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </div>

                {/* Department Cards Grid */}
                <div className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-sm border border-slate-100">
                  <h3 className="text-sm sm:text-lg font-semibold text-slate-900 mb-4 sm:mb-6 flex items-center gap-2">
                    <Building className="h-4 w-4 sm:h-5 sm:w-5 text-slate-400" />
                    All Departments
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
                    {departmentStats
                      .filter(
                        (dept) =>
                          searchQuery === "" ||
                          dept.name
                            .toLowerCase()
                            .includes(searchQuery.toLowerCase()) ||
                          dept.code
                            .toLowerCase()
                            .includes(searchQuery.toLowerCase()),
                      )
                      .map((dept, index) => {
                        const colors = [
                          "from-violet-500 to-purple-600",
                          "from-blue-500 to-cyan-600",
                          "from-emerald-500 to-teal-600",
                          "from-amber-500 to-orange-600",
                          "from-rose-500 to-pink-600",
                          "from-indigo-500 to-blue-600",
                        ];
                        const bgColors = [
                          "bg-violet-50 border-violet-200",
                          "bg-blue-50 border-blue-200",
                          "bg-emerald-50 border-emerald-200",
                          "bg-amber-50 border-amber-200",
                          "bg-rose-50 border-rose-200",
                          "bg-indigo-50 border-indigo-200",
                        ];
                        const colorIndex = index % colors.length;
                        return (
                          <div
                            key={dept._id}
                            className={`${bgColors[colorIndex]} p-5 rounded-2xl border transition-all duration-300 hover:shadow-lg hover:scale-[1.02]`}
                          >
                            <div className="flex items-start justify-between mb-4">
                              <div className="flex-1">
                                <h4 className="text-base font-bold text-slate-900 line-clamp-1">
                                  {dept.name}
                                </h4>
                                <p className="text-xs text-slate-500 font-medium mt-0.5">
                                  Code: {dept.code}
                                </p>
                              </div>
                              <div
                                className={`bg-gradient-to-br ${colors[colorIndex]} rounded-xl p-2.5 shadow-lg`}
                              >
                                <Building className="h-5 w-5 text-white" />
                              </div>
                            </div>

                            {dept.statistics && (
                              <div className="grid grid-cols-2 gap-2 mb-4">
                                <div className="bg-white/80 backdrop-blur-sm p-3 rounded-xl text-center">
                                  <p className="text-xl font-bold text-slate-900">
                                    {dept.statistics.totalStudents || 0}
                                  </p>
                                  <p className="text-xs text-slate-500">
                                    Students
                                  </p>
                                </div>
                                <div className="bg-white/80 backdrop-blur-sm p-3 rounded-xl text-center">
                                  <p className="text-xl font-bold text-emerald-600">
                                    {dept.statistics.activeStudents || 0}
                                  </p>
                                  <p className="text-xs text-slate-500">
                                    Active
                                  </p>
                                </div>
                                <div className="bg-white/80 backdrop-blur-sm p-3 rounded-xl text-center">
                                  <p className="text-xl font-bold text-purple-600">
                                    {dept.statistics.totalTeachers || 0}
                                  </p>
                                  <p className="text-xs text-slate-500">
                                    Teachers
                                  </p>
                                </div>
                                <div className="bg-white/80 backdrop-blur-sm p-3 rounded-xl text-center">
                                  <p className="text-xl font-bold text-amber-600">
                                    {dept.statistics.totalCourses || 0}
                                  </p>
                                  <p className="text-xs text-slate-500">
                                    Courses
                                  </p>
                                </div>
                              </div>
                            )}

                            {dept.headOfDepartment ? (
                              <div className="flex items-center gap-3 pt-3 border-t border-slate-200/50">
                                {dept.headOfDepartment.profilePicture ? (
                                  <img
                                    src={dept.headOfDepartment.profilePicture}
                                    alt={`${dept.headOfDepartment.firstName} ${dept.headOfDepartment.lastName}`}
                                    className="w-8 h-8 rounded-full object-cover"
                                    onError={(e) => {
                                      (
                                        e.target as HTMLImageElement
                                      ).style.display = "none";
                                      (
                                        e.target as HTMLImageElement
                                      ).nextElementSibling?.classList.remove(
                                        "hidden",
                                      );
                                    }}
                                  />
                                ) : null}
                                <div
                                  className={`w-8 h-8 rounded-full bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center text-white text-xs font-bold ${
                                    dept.headOfDepartment.profilePicture
                                      ? "hidden"
                                      : ""
                                  }`}
                                >
                                  {dept.headOfDepartment.firstName?.charAt(0)}
                                  {dept.headOfDepartment.lastName?.charAt(0)}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs text-slate-500">
                                    Head of Department
                                  </p>
                                  <p className="text-sm font-semibold text-slate-900 truncate">
                                    {dept.headOfDepartment.firstName}{" "}
                                    {dept.headOfDepartment.lastName}
                                  </p>
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-center gap-3 pt-3 border-t border-slate-200/50">
                                <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                                  <UserX className="h-4 w-4 text-slate-400" />
                                </div>
                                <div className="flex-1">
                                  <p className="text-xs text-slate-400">
                                    Head of Department
                                  </p>
                                  <p className="text-sm text-slate-400 italic">
                                    Not assigned
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </div>
                </div>

                {/* Quick Stats Footer */}
                <div className="bg-gradient-to-r from-violet-50 to-purple-50 rounded-3xl p-4 sm:p-6 border border-violet-100">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-6">
                    <div className="text-center">
                      <p className="text-xl sm:text-3xl font-bold text-slate-900">
                        {departmentStats.length}
                      </p>
                      <p className="text-xs sm:text-sm text-slate-500 mt-1">
                        Total Departments
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-xl sm:text-3xl font-bold text-slate-900">
                        {departmentStats.reduce(
                          (sum, d) => sum + (d.statistics?.totalCourses || 0),
                          0,
                        )}
                      </p>
                      <p className="text-xs sm:text-sm text-slate-500 mt-1">
                        Total Courses
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-xl sm:text-3xl font-bold text-slate-900">
                        {
                          departmentStats.filter((d) => d.headOfDepartment)
                            .length
                        }
                      </p>
                      <p className="text-xs sm:text-sm text-slate-500 mt-1">
                        HODs Assigned
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-xl sm:text-3xl font-bold text-slate-900">
                        {departmentStats.length > 0
                          ? Math.round(
                              departmentStats.reduce(
                                (sum, d) =>
                                  sum + (d.statistics?.totalStudents || 0),
                                0,
                              ) / departmentStats.length,
                            )
                          : 0}
                      </p>
                      <p className="text-xs sm:text-sm text-slate-500 mt-1">
                        Avg Students/Dept
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {/* Staff Metrics Tab - Apple-like Clean Design */}
            {activeTab === "teachers" &&
              (teacherStats || departmentStats.length > 0) && (
                <div className="space-y-4 sm:space-y-6">
                  {/* Hero Metrics Row */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                    {/* Total Staff - Large Dark Card */}
                    <div className="sm:col-span-2 lg:col-span-1 bg-gradient-to-br from-indigo-900 to-purple-900 rounded-2xl p-4 sm:p-6 text-white shadow-lg">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-indigo-200 text-xs font-medium tracking-wide uppercase">
                            Total Staff
                          </p>
                          <p className="text-2xl sm:text-3xl font-bold mt-1 tracking-tight">
                            {effectiveTeacherCount}
                          </p>
                          <p className="text-indigo-200 text-xs mt-1">
                            Teachers & Staff
                          </p>
                        </div>
                        <div className="bg-white/10 rounded-xl p-2 sm:p-2.5 flex-shrink-0">
                          <Users className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                        </div>
                      </div>
                      <div className="mt-4 pt-4 border-t border-indigo-700">
                        <div className="flex items-center justify-between">
                          <span className="text-indigo-200 text-xs">
                            Active Rate
                          </span>
                          <span className="text-emerald-400 font-semibold text-sm">
                            {effectiveTeacherCount > 0
                              ? Math.round(
                                  (effectiveActiveTeachers /
                                    effectiveTeacherCount) *
                                    100,
                                )
                              : 0}
                            %
                          </span>
                        </div>
                        <div className="mt-1.5 h-1.5 bg-indigo-700 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full transition-all duration-500"
                            style={{
                              width: `${
                                effectiveTeacherCount > 0
                                  ? (effectiveActiveTeachers /
                                      effectiveTeacherCount) *
                                    100
                                  : 0
                              }%`,
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Student-to-Teacher Ratio */}
                    <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm border border-slate-100">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-slate-500 text-xs font-medium tracking-wide uppercase">
                            Student-Teacher Ratio
                          </p>
                          <p className="text-2xl sm:text-3xl font-bold text-slate-900 mt-1 tracking-tight">
                            {effectiveStudentTeacherRatio}:1
                          </p>
                          <p className="text-slate-500 text-xs mt-1">
                            Students per teacher
                          </p>
                        </div>
                        <div
                          className={`rounded-xl p-2 sm:p-2.5 flex-shrink-0 ${
                            effectiveStudentTeacherRatio <= 20
                              ? "bg-emerald-50"
                              : effectiveStudentTeacherRatio <= 30
                                ? "bg-amber-50"
                                : "bg-red-50"
                          }`}
                        >
                          <GraduationCap
                            className={`h-5 w-5 sm:h-6 sm:w-6 ${
                              effectiveStudentTeacherRatio <= 20
                                ? "text-emerald-600"
                                : effectiveStudentTeacherRatio <= 30
                                  ? "text-amber-600"
                                  : "text-red-600"
                            }`}
                          />
                        </div>
                      </div>
                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                            effectiveStudentTeacherRatio <= 20
                              ? "bg-emerald-100 text-emerald-700"
                              : effectiveStudentTeacherRatio <= 30
                                ? "bg-amber-100 text-amber-700"
                                : "bg-red-100 text-red-700"
                          }`}
                        >
                          {effectiveStudentTeacherRatio <= 20
                            ? "Excellent"
                            : effectiveStudentTeacherRatio <= 30
                              ? "Good"
                              : "High"}
                        </span>
                        <span className="text-slate-500 text-xs">
                          {studentStats?.totalStudents ||
                            teacherStats?.totalActiveStudents ||
                            0}{" "}
                          active students
                        </span>
                      </div>
                    </div>

                    {/* Staff Attendance Rate */}
                    <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm border border-slate-100">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-slate-500 text-xs font-medium tracking-wide uppercase">
                            Attendance Rate
                          </p>
                          <p className="text-2xl sm:text-3xl font-bold text-slate-900 mt-1 tracking-tight">
                            {teacherStats?.averageAttendanceRate || 0}%
                          </p>
                          <p className="text-slate-500 text-xs mt-1">
                            Average staff attendance
                          </p>
                        </div>
                        <div
                          className={`rounded-xl p-2 sm:p-2.5 flex-shrink-0 ${
                            (teacherStats?.averageAttendanceRate || 0) >= 90
                              ? "bg-emerald-50"
                              : (teacherStats?.averageAttendanceRate || 0) >= 75
                                ? "bg-amber-50"
                                : "bg-red-50"
                          }`}
                        >
                          <Calendar
                            className={`h-5 w-5 sm:h-6 sm:w-6 ${
                              (teacherStats?.averageAttendanceRate || 0) >= 90
                                ? "text-emerald-600"
                                : (teacherStats?.averageAttendanceRate || 0) >=
                                    75
                                  ? "text-amber-600"
                                  : "text-red-600"
                            }`}
                          />
                        </div>
                      </div>
                      <div className="mt-3 sm:mt-4">
                        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all duration-500 ${
                              (teacherStats?.averageAttendanceRate || 0) >= 90
                                ? "bg-emerald-500"
                                : (teacherStats?.averageAttendanceRate || 0) >=
                                    75
                                  ? "bg-amber-500"
                                  : "bg-red-500"
                            }`}
                            style={{
                              width: `${
                                teacherStats?.averageAttendanceRate || 0
                              }%`,
                            }}
                          />
                        </div>
                        <div className="flex justify-between mt-1.5 text-[10px] sm:text-xs text-slate-500">
                          <span>
                            Present:{" "}
                            {teacherStats?.attendanceSummary?.totalPresent || 0}
                          </span>
                          <span>
                            Absent:{" "}
                            {teacherStats?.attendanceSummary?.totalAbsent || 0}
                          </span>
                          <span>
                            Late:{" "}
                            {teacherStats?.attendanceSummary?.totalLate || 0}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Employment Status & Type Row */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
                    {/* Employment Status Breakdown */}
                    <div className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-sm border border-slate-100">
                      <h3 className="text-sm sm:text-lg font-semibold text-slate-900 mb-4 sm:mb-6">
                        Staff by Status
                      </h3>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-4">
                        {[
                          { key: "active", label: "Active", color: "emerald" },
                          {
                            key: "inactive",
                            label: "Inactive",
                            color: "slate",
                          },
                          {
                            key: "on_leave",
                            label: "On Leave",
                            color: "amber",
                          },
                          {
                            key: "resigned",
                            label: "Resigned",
                            color: "orange",
                          },
                          {
                            key: "terminated",
                            label: "Terminated",
                            color: "red",
                          },
                          { key: "retired", label: "Retired", color: "blue" },
                        ].map((status) => {
                          const count =
                            teacherStats?.statusBreakdown?.[status.key] ??
                            teacherStats?.statusCounts?.[status.key] ??
                            0;
                          const colorClasses: Record<
                            string,
                            { bg: string; text: string }
                          > = {
                            emerald: {
                              bg: "bg-emerald-50",
                              text: "text-emerald-700",
                            },
                            slate: {
                              bg: "bg-slate-50",
                              text: "text-slate-700",
                            },
                            amber: {
                              bg: "bg-amber-50",
                              text: "text-amber-700",
                            },
                            orange: {
                              bg: "bg-orange-50",
                              text: "text-orange-700",
                            },
                            red: { bg: "bg-red-50", text: "text-red-700" },
                            blue: { bg: "bg-blue-50", text: "text-blue-700" },
                          };
                          const colors = colorClasses[status.color];
                          return (
                            <div
                              key={status.key}
                              className={`${colors.bg} rounded-2xl p-4 text-center transition-transform hover:scale-105`}
                            >
                              <p
                                className={`text-2xl font-bold ${colors.text}`}
                              >
                                {count}
                              </p>
                              <p className="text-xs font-medium text-slate-500 mt-1">
                                {status.label}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Employment Type Breakdown */}
                    <div className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-sm border border-slate-100">
                      <h3 className="text-sm sm:text-lg font-semibold text-slate-900 mb-4 sm:mb-6">
                        Employment Type
                      </h3>
                      <div className="space-y-3 sm:space-y-4">
                        {[
                          {
                            key: "full_time",
                            label: "Full Time",
                            color: "from-blue-500 to-indigo-500",
                          },
                          {
                            key: "part_time",
                            label: "Part Time",
                            color: "from-purple-500 to-pink-500",
                          },
                          {
                            key: "contract",
                            label: "Contract",
                            color: "from-amber-500 to-orange-500",
                          },
                          {
                            key: "substitute",
                            label: "Substitute",
                            color: "from-emerald-500 to-teal-500",
                          },
                        ].map((type) => {
                          const count =
                            teacherStats?.employmentTypeBreakdown?.[type.key] ??
                            0;
                          const total = effectiveTeacherCount || 1;
                          const percentage = (count / total) * 100;
                          return (
                            <div key={type.key} className="group">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-xs sm:text-sm font-medium text-slate-700">
                                  {type.label}
                                </span>
                                <span className="text-xs sm:text-sm font-bold text-slate-900">
                                  {count}
                                </span>
                              </div>
                              <div className="h-1.5 sm:h-2 bg-slate-100 rounded-full overflow-hidden">
                                <div
                                  className={`h-full bg-gradient-to-r ${type.color} rounded-full transition-all duration-500 group-hover:opacity-80`}
                                  style={{ width: `${percentage}%` }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Staff by Department */}
                  <div className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-sm border border-slate-100">
                    <h3 className="text-sm sm:text-lg font-semibold text-slate-900 mb-4 sm:mb-6 flex items-center gap-2">
                      <Building className="h-4 w-4 sm:h-5 sm:w-5 text-slate-400" />
                      Staff by Department
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                      {/* Bar Chart View */}
                      <div className="space-y-3 sm:space-y-4">
                        {(
                          teacherStats?.departmentCounts ||
                          departmentStats?.map((d: any) => ({
                            departmentName: d.name,
                            departmentCode: d.code,
                            count:
                              d.staffCount || d.statistics?.totalTeachers || 0,
                          })) ||
                          []
                        )
                          .slice(0, 8)
                          .map((dept: any, index: number) => {
                            const deptData =
                              teacherStats?.departmentCounts ||
                              departmentStats?.map((d: any) => ({
                                count:
                                  d.staffCount ||
                                  d.statistics?.totalTeachers ||
                                  0,
                              })) ||
                              [];
                            const maxCount = Math.max(
                              ...deptData.map((d: any) => d.count || 0),
                            );
                            const percentage =
                              maxCount > 0
                                ? ((dept.count || 0) / maxCount) * 100
                                : 0;
                            const colors = [
                              "from-blue-500 to-cyan-500",
                              "from-purple-500 to-pink-500",
                              "from-emerald-500 to-teal-500",
                              "from-amber-500 to-orange-500",
                              "from-rose-500 to-red-500",
                              "from-indigo-500 to-purple-500",
                              "from-cyan-500 to-blue-500",
                              "from-pink-500 to-rose-500",
                            ];
                            return (
                              <div
                                key={dept.departmentId || index}
                                className="group"
                              >
                                <div className="flex items-center justify-between mb-1.5">
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium text-slate-700 truncate max-w-[200px]">
                                      {dept.departmentName || "Unassigned"}
                                    </span>
                                    {dept.departmentCode &&
                                      dept.departmentCode !== "N/A" && (
                                        <span className="text-xs text-slate-400">
                                          ({dept.departmentCode})
                                        </span>
                                      )}
                                  </div>
                                  <span className="text-sm font-bold text-slate-900">
                                    {dept.count || 0}
                                  </span>
                                </div>
                                <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                                  <div
                                    className={`h-full bg-gradient-to-r ${
                                      colors[index % colors.length]
                                    } rounded-full transition-all duration-500 group-hover:opacity-80`}
                                    style={{ width: `${percentage}%` }}
                                  />
                                </div>
                              </div>
                            );
                          })}
                        {!teacherStats?.departmentCounts &&
                          (!departmentStats ||
                            departmentStats.length === 0) && (
                            <p className="text-slate-400 text-center py-8">
                              No department data available
                            </p>
                          )}
                      </div>

                      {/* Circular Summary */}
                      <div className="flex flex-col items-center justify-center">
                        <div className="relative w-48 h-48">
                          <svg
                            className="w-48 h-48 transform -rotate-90"
                            viewBox="0 0 36 36"
                          >
                            <circle
                              cx="18"
                              cy="18"
                              r="15.5"
                              fill="none"
                              stroke="#f1f5f9"
                              strokeWidth="3"
                            />
                            {(
                              teacherStats?.departmentCounts ||
                              departmentStats?.map((d: any) => ({
                                count:
                                  d.staffCount ||
                                  d.statistics?.totalTeachers ||
                                  0,
                              })) ||
                              []
                            )
                              .slice(0, 5)
                              .reduce(
                                (acc: any[], dept: any, index: number) => {
                                  const total = effectiveTeacherCount || 1;
                                  const percentage =
                                    ((dept.count || 0) / total) * 100;
                                  const offset =
                                    acc.length > 0
                                      ? acc[acc.length - 1].offset +
                                        acc[acc.length - 1].dashArray
                                      : 0;
                                  const colors = [
                                    "#3b82f6",
                                    "#8b5cf6",
                                    "#10b981",
                                    "#f59e0b",
                                    "#ef4444",
                                  ];
                                  acc.push({
                                    color: colors[index % colors.length],
                                    dashArray: percentage,
                                    offset: offset,
                                  });
                                  return acc;
                                },
                                [],
                              )
                              .map((segment: any, index: number) => (
                                <circle
                                  key={index}
                                  cx="18"
                                  cy="18"
                                  r="15.5"
                                  fill="none"
                                  stroke={segment.color}
                                  strokeWidth="3"
                                  strokeDasharray={`${segment.dashArray} 100`}
                                  strokeDashoffset={`-${segment.offset}`}
                                  strokeLinecap="round"
                                />
                              ))}
                          </svg>
                          <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <span className="text-3xl font-bold text-slate-900">
                              {effectiveTeacherCount}
                            </span>
                            <span className="text-sm text-slate-500">
                              Total Staff
                            </span>
                          </div>
                        </div>

                        {/* Legend */}
                        <div className="mt-6 flex flex-wrap justify-center gap-3">
                          {(
                            teacherStats?.departmentCounts ||
                            departmentStats?.map((d: any) => ({
                              departmentName: d.name,
                              count:
                                d.staffCount ||
                                d.statistics?.totalTeachers ||
                                0,
                            })) ||
                            []
                          )
                            .slice(0, 5)
                            .map((dept: any, index: number) => {
                              const colors = [
                                "bg-blue-500",
                                "bg-purple-500",
                                "bg-emerald-500",
                                "bg-amber-500",
                                "bg-red-500",
                              ];
                              return (
                                <div
                                  key={dept.departmentId || index}
                                  className="flex items-center gap-1.5"
                                >
                                  <div
                                    className={`w-2.5 h-2.5 rounded-full ${
                                      colors[index % colors.length]
                                    }`}
                                  />
                                  <span className="text-xs text-slate-600">
                                    {dept.departmentName || "Other"}
                                  </span>
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Quick Stats Footer */}
                  <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-3xl p-4 sm:p-6 border border-indigo-100">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-6">
                      <div className="text-center">
                        <p className="text-xl sm:text-3xl font-bold text-slate-900">
                          {teacherStats?.newHiresThisMonth || 0}
                        </p>
                        <p className="text-xs sm:text-sm text-slate-500 mt-1">
                          New This Month
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-xl sm:text-3xl font-bold text-slate-900">
                          {teacherStats?.newHiresThisYear || 0}
                        </p>
                        <p className="text-xs sm:text-sm text-slate-500 mt-1">
                          New This Year
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-xl sm:text-3xl font-bold text-slate-900">
                          {teacherStats?.averageExperience || 0}y
                        </p>
                        <p className="text-xs sm:text-sm text-slate-500 mt-1">
                          Avg Experience
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-xl sm:text-3xl font-bold text-slate-900">
                          {departmentStats?.length || 0}
                        </p>
                        <p className="text-xs sm:text-sm text-slate-500 mt-1">
                          Departments
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            {/* Course Analytics Tab - Apple-like Clean Design */}
            {activeTab === "courses" && courseStats.length > 0 && (
              <div className="space-y-4 sm:space-y-6">
                {/* Hero Metrics Row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  {/* Total Courses - Large Dark Card */}
                  <div className="sm:col-span-2 lg:col-span-1 bg-gradient-to-br from-cyan-900 to-teal-900 rounded-2xl p-4 sm:p-6 text-white shadow-lg">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-cyan-200 text-xs font-medium tracking-wide uppercase">
                          Total Courses
                        </p>
                        <p className="text-2xl sm:text-3xl font-bold mt-1 tracking-tight">
                          {courseStats.length}
                        </p>
                        <p className="text-cyan-200 text-xs mt-1">
                          Academic programs
                        </p>
                      </div>
                      <div className="bg-white/10 rounded-xl p-2 sm:p-2.5 flex-shrink-0">
                        <BookOpen className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-cyan-700">
                      <div className="flex items-center justify-between">
                        <span className="text-cyan-200 text-xs">
                          Active Rate
                        </span>
                        <span className="text-emerald-400 font-semibold text-sm">
                          {courseStats.length > 0
                            ? Math.round(
                                (courseStats.filter(
                                  (c: any) => c.isActive !== false,
                                ).length /
                                  courseStats.length) *
                                  100,
                              )
                            : 0}
                          %
                        </span>
                      </div>
                      <div className="mt-1.5 h-1.5 bg-cyan-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full transition-all duration-500"
                          style={{
                            width: `${
                              courseStats.length > 0
                                ? (courseStats.filter(
                                    (c: any) => c.isActive !== false,
                                  ).length /
                                    courseStats.length) *
                                  100
                                : 0
                            }%`,
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Active Courses */}
                  <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm border border-slate-100">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-slate-500 text-xs font-medium tracking-wide uppercase">
                          Active Courses
                        </p>
                        <p className="text-2xl sm:text-3xl font-bold text-slate-900 mt-1 tracking-tight">
                          {
                            courseStats.filter((c: any) => c.isActive !== false)
                              .length
                          }
                        </p>
                        <p className="text-slate-500 text-xs mt-1">
                          Currently offered
                        </p>
                      </div>
                      <div className="bg-emerald-50 rounded-xl p-2 sm:p-2.5 flex-shrink-0">
                        <UserCheck className="h-5 w-5 sm:h-6 sm:w-6 text-emerald-600" />
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">
                        {
                          courseStats.filter((c: any) => c.isActive === false)
                            .length
                        }{" "}
                        Inactive
                      </span>
                      <span className="text-slate-500 text-xs">
                        courses paused
                      </span>
                    </div>
                  </div>

                  {/* Course Levels */}
                  <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm border border-slate-100">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-slate-500 text-xs font-medium tracking-wide uppercase">
                          Course Levels
                        </p>
                        <p className="text-2xl sm:text-3xl font-bold text-slate-900 mt-1 tracking-tight">
                          {
                            [
                              ...new Set(
                                courseStats
                                  .map((c: any) => c.level)
                                  .filter(Boolean),
                              ),
                            ].length
                          }
                        </p>
                        <p className="text-slate-500 text-xs mt-1">
                          Academic tiers
                        </p>
                      </div>
                      <div className="bg-purple-50 rounded-xl p-2 sm:p-2.5 flex-shrink-0">
                        <Award className="h-5 w-5 sm:h-6 sm:w-6 text-purple-600" />
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-700">
                        {courseStats.reduce(
                          (sum: number, c: any) => sum + (c.credits || 0),
                          0,
                        )}{" "}
                        Total Credits
                      </span>
                    </div>
                  </div>
                </div>

                {/* Course Distribution & Level Breakdown */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
                  {/* Courses by Level Chart */}
                  <div className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-sm border border-slate-100">
                    <h3 className="text-sm sm:text-lg font-semibold text-slate-900 mb-4 sm:mb-6 flex items-center gap-2">
                      <BarChart3 className="h-4 w-4 sm:h-5 sm:w-5 text-slate-400" />
                      Courses by Level
                    </h3>
                    <div className="space-y-3 sm:space-y-4">
                      {(() => {
                        const levelCounts = courseStats.reduce(
                          (acc: any, course: any) => {
                            const level = course.level || "Unspecified";
                            acc[level] = (acc[level] || 0) + 1;
                            return acc;
                          },
                          {},
                        );
                        const sortedLevels = Object.entries(levelCounts)
                          .sort((a: any, b: any) => b[1] - a[1])
                          .slice(0, 6);
                        const maxCount = Math.max(
                          ...(Object.values(levelCounts) as number[]),
                        );
                        const colors = [
                          "from-cyan-500 to-teal-500",
                          "from-blue-500 to-indigo-500",
                          "from-purple-500 to-pink-500",
                          "from-amber-500 to-orange-500",
                          "from-emerald-500 to-green-500",
                          "from-rose-500 to-red-500",
                        ];
                        return sortedLevels.map(
                          ([level, count]: [string, any], index: number) => (
                            <div key={level} className="group">
                              <div className="flex items-center justify-between mb-1.5">
                                <span className="text-sm font-medium text-slate-700 truncate max-w-[200px]">
                                  {level}
                                </span>
                                <span className="text-sm font-bold text-slate-900">
                                  {count} {count === 1 ? "course" : "courses"}
                                </span>
                              </div>
                              <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                                <div
                                  className={`h-full bg-gradient-to-r ${
                                    colors[index % colors.length]
                                  } rounded-full transition-all duration-500 group-hover:opacity-80`}
                                  style={{
                                    width: `${(count / maxCount) * 100}%`,
                                  }}
                                />
                              </div>
                            </div>
                          ),
                        );
                      })()}
                    </div>
                  </div>

                  {/* Top Courses by Credits */}
                  <div className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-sm border border-slate-100">
                    <h3 className="text-sm sm:text-lg font-semibold text-slate-900 mb-4 sm:mb-6 flex items-center gap-2">
                      <Award className="h-4 w-4 sm:h-5 sm:w-5 text-amber-500" />
                      Highest Credit Courses
                    </h3>
                    <div className="space-y-3 sm:space-y-4">
                      {[...courseStats]
                        .filter((c: any) => c.credits)
                        .sort(
                          (a: any, b: any) =>
                            (b.credits || 0) - (a.credits || 0),
                        )
                        .slice(0, 5)
                        .map((course: any, index: number) => {
                          const badges = ["🥇", "🥈", "🥉", "4th", "5th"];
                          return (
                            <div
                              key={course._id}
                              className="flex items-center gap-2 sm:gap-4"
                            >
                              <span className="text-base sm:text-2xl w-6 sm:w-8">
                                {badges[index]}
                              </span>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between mb-0.5 sm:mb-1">
                                  <span className="font-medium text-slate-900 text-xs sm:text-sm truncate max-w-[140px] sm:max-w-none">
                                    {course.name}
                                  </span>
                                  <span className="text-xs sm:text-sm font-bold text-cyan-600 ml-2 flex-shrink-0">
                                    {course.credits} credits
                                  </span>
                                </div>
                                <p className="text-[10px] sm:text-xs text-slate-500 truncate">
                                  {course.code} • {course.level || "N/A"}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      {courseStats.filter((c: any) => c.credits).length ===
                        0 && (
                        <p className="text-slate-400 text-center py-4 text-sm">
                          No credit information available
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Course Cards Grid */}
                <div className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-sm border border-slate-100">
                  <div className="flex items-center justify-between mb-4 sm:mb-6">
                    <h3 className="text-sm sm:text-lg font-semibold text-slate-900 flex items-center gap-2">
                      <BookOpen className="h-4 w-4 sm:h-5 sm:w-5 text-slate-400" />
                      All Courses
                    </h3>
                    <span className="text-xs sm:text-sm text-slate-500">
                      Showing{" "}
                      {Math.min(
                        courseStats.filter(
                          (course: any) =>
                            searchQuery === "" ||
                            course.name
                              ?.toLowerCase()
                              .includes(searchQuery.toLowerCase()) ||
                            course.code
                              ?.toLowerCase()
                              .includes(searchQuery.toLowerCase()),
                        ).length,
                        24,
                      )}{" "}
                      of {courseStats.length}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
                    {courseStats
                      .filter(
                        (course: any) =>
                          searchQuery === "" ||
                          course.name
                            ?.toLowerCase()
                            .includes(searchQuery.toLowerCase()) ||
                          course.code
                            ?.toLowerCase()
                            .includes(searchQuery.toLowerCase()),
                      )
                      .slice(0, 24)
                      .map((course: any, index: number) => {
                        const colors = [
                          "from-cyan-500 to-teal-600",
                          "from-blue-500 to-indigo-600",
                          "from-purple-500 to-pink-600",
                          "from-amber-500 to-orange-600",
                          "from-emerald-500 to-green-600",
                          "from-rose-500 to-red-600",
                        ];
                        const bgColors = [
                          "bg-cyan-50 border-cyan-200",
                          "bg-blue-50 border-blue-200",
                          "bg-purple-50 border-purple-200",
                          "bg-amber-50 border-amber-200",
                          "bg-emerald-50 border-emerald-200",
                          "bg-rose-50 border-rose-200",
                        ];
                        const colorIndex = index % colors.length;
                        return (
                          <div
                            key={course._id}
                            className={`${bgColors[colorIndex]} p-5 rounded-2xl border transition-all duration-300 hover:shadow-lg hover:scale-[1.02]`}
                          >
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex-1 min-w-0">
                                <h4 className="text-base font-bold text-slate-900 line-clamp-1">
                                  {course.name}
                                </h4>
                                <p className="text-xs text-slate-500 font-medium mt-0.5">
                                  Code: {course.code}
                                </p>
                              </div>
                              <div
                                className={`bg-gradient-to-br ${colors[colorIndex]} rounded-xl p-2 shadow-lg ml-2`}
                              >
                                <BookOpen className="h-4 w-4 text-white" />
                              </div>
                            </div>

                            <div className="grid grid-cols-3 gap-2 mb-3">
                              <div className="bg-white/80 backdrop-blur-sm p-2.5 rounded-xl text-center">
                                <p className="text-lg font-bold text-slate-900">
                                  {course.credits || "-"}
                                </p>
                                <p className="text-xs text-slate-500">
                                  Credits
                                </p>
                              </div>
                              <div className="bg-white/80 backdrop-blur-sm p-2.5 rounded-xl text-center">
                                <p className="text-lg font-bold text-slate-900 truncate">
                                  {course.level || "-"}
                                </p>
                                <p className="text-xs text-slate-500">Level</p>
                              </div>
                              <div className="bg-white/80 backdrop-blur-sm p-2.5 rounded-xl text-center">
                                <p className="text-lg font-bold text-slate-900">
                                  {course.enrolledStudents || 0}
                                </p>
                                <p className="text-xs text-slate-500">
                                  Students
                                </p>
                              </div>
                            </div>

                            <div className="flex items-center justify-between pt-3 border-t border-slate-200/50">
                              <span
                                className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold ${
                                  course.isActive !== false
                                    ? "bg-emerald-100 text-emerald-700"
                                    : "bg-slate-100 text-slate-600"
                                }`}
                              >
                                {course.isActive !== false
                                  ? "Active"
                                  : "Inactive"}
                              </span>
                              {course.department?.name && (
                                <span className="text-xs text-slate-500 truncate max-w-[100px]">
                                  {course.department.name}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>

                {/* Quick Stats Footer */}
                <div className="bg-gradient-to-r from-cyan-50 to-teal-50 rounded-3xl p-4 sm:p-6 border border-cyan-100">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-6">
                    <div className="text-center">
                      <p className="text-xl sm:text-3xl font-bold text-slate-900">
                        {courseStats.length}
                      </p>
                      <p className="text-xs sm:text-sm text-slate-500 mt-1">
                        Total Courses
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-xl sm:text-3xl font-bold text-slate-900">
                        {
                          courseStats.filter((c: any) => c.isActive !== false)
                            .length
                        }
                      </p>
                      <p className="text-xs sm:text-sm text-slate-500 mt-1">
                        Active Courses
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-xl sm:text-3xl font-bold text-slate-900">
                        {
                          [
                            ...new Set(
                              courseStats
                                .map((c: any) => c.level)
                                .filter(Boolean),
                            ),
                          ].length
                        }
                      </p>
                      <p className="text-xs sm:text-sm text-slate-500 mt-1">
                        Course Levels
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-xl sm:text-3xl font-bold text-slate-900">
                        {courseStats.length > 0
                          ? Math.round(
                              courseStats.reduce(
                                (sum: number, c: any) => sum + (c.credits || 0),
                                0,
                              ) /
                                courseStats.filter((c: any) => c.credits)
                                  .length || 1,
                            )
                          : 0}
                      </p>
                      <p className="text-xs sm:text-sm text-slate-500 mt-1">
                        Avg Credits
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {/* Financial Tab (Original Content) */}
            {activeTab === "financial" && reportData && (
              <div className="space-y-4 sm:space-y-6">
                <div className="bg-white p-4 sm:p-6 rounded-2xl shadow-lg border border-slate-200">
                  <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-4">
                    Financial Overview
                  </h3>
                  {reportData.summary && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-6">
                      <div className="text-center p-4 sm:p-6 bg-emerald-50 rounded-xl border border-emerald-200">
                        <p className="text-xs sm:text-sm font-bold text-emerald-700">
                          Total Revenue
                        </p>
                        <p className="text-xl sm:text-3xl font-bold text-emerald-900 mt-2">
                          {formatCurrency(reportData.summary.totalRevenue)}
                        </p>
                      </div>
                      <div className="text-center p-4 sm:p-6 bg-red-50 rounded-xl border border-red-200">
                        <p className="text-xs sm:text-sm font-bold text-red-700">
                          Total Expenses
                        </p>
                        <p className="text-xl sm:text-3xl font-bold text-red-900 mt-2">
                          {formatCurrency(reportData.summary.totalExpenses)}
                        </p>
                      </div>
                      <div className="text-center p-4 sm:p-6 bg-blue-50 rounded-xl border border-blue-200">
                        <p className="text-xs sm:text-sm font-bold text-blue-700">
                          Net Profit
                        </p>
                        <p className="text-xl sm:text-3xl font-bold text-blue-900 mt-2">
                          {formatCurrency(reportData.summary.netProfit)}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* No Data State */}
      {!loading && !reportData && (
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-12">
          <div className="text-center">
            <div className="bg-gradient-to-br from-slate-300 to-slate-400 rounded-2xl p-4 w-fit mx-auto mb-4">
              <BarChart3 className="h-12 w-12 text-white" />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">
              No Report Data Available
            </h3>
            <p className="text-slate-600 font-medium mb-6">
              No financial data found for the selected time period.
            </p>
            <button
              onClick={loadReportsData}
              className="bg-white border-2 border-slate-200 px-5 py-2.5 rounded-xl font-bold shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2 mx-auto"
            >
              <RefreshCw className="h-5 w-5 text-blue-500" />
              <span className="text-slate-700">Reload Reports</span>
            </button>
          </div>
        </div>
      )}

      <AiAnalyticsModal
        isOpen={isAiModalOpen}
        onClose={() => setIsAiModalOpen(false)}
        branchId={effectiveBranchId}
      />
    </div>
  );
};

export default BranchReports;
